// src/pages/DepartamentosRFID.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
    FaBroadcastTower, 
    FaDownload, 
    FaChevronLeft, 
    FaExclamationTriangle, 
    FaShieldAlt, 
    FaServer, 
    FaCircle, 
    FaSync, 
    FaTrashAlt, 
    FaDoorOpen, 
    FaHistory, 
    FaCheckCircle,
    FaSearchPlus,
    FaTerminal,
    FaQrcode,
    FaDatabase,
    FaFilter
} from 'react-icons/fa';
import './DepartamentosDetalle.css';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { 
    collection, 
    getDocs, 
    orderBy, 
    query, 
    doc, 
    getDoc, 
    setDoc, 
    onSnapshot, 
    limit, 
    addDoc, 
    serverTimestamp,
    writeBatch
} from 'firebase/firestore';
import { toCsv, downloadCsv } from '../utils/exportCsv';
import { calcularAdeudoVisibleDesdeObjeto } from '../utils/calcularAdeudoVisible';
import { calcularEstadoMoroso } from '../utils/morosoUtils';
import { registerMovement } from '../utils/registerMovement';
import { convertirTagAntenaAImpreso } from '../components/ResidentProfileModal';
/**
 * Resuelve dinámicamente el nombre de la puerta, el nombre limpio del panel,
 * su icono representativo y color de estado para los registros de accesos.
 */
export const resolverPuntoAccesoInfo = (log, panelConfig) => {
    if (!log) {
        return {
            nombrePuerta: 'Acceso Desconocido',
            nombrePanel: 'Panel',
            textoCompleto: 'Acceso Desconocido',
            icono: '🚪',
            color: '#64748b'
        };
    }

    const puertaNum = parseInt(log.puerta || 1, 10);
    const dirLower = String(log.direccion || '').toLowerCase();
    const puntoLower = String(log.puntoAcceso || '').toLowerCase();
    const esSalida = dirLower === 'salida' || puertaNum === 2 || puntoLower.includes('salida');
    const esBasura = log.panelId === 'panel_2' || puntoLower.includes('basura');
    const es4Puertas = log.panelId === 'panel_4puertas' || log.panelId === 'panel4p' || (log.panel && String(log.panel).includes('DS-K2814'));

    // 1. Determinar Nombre de Puerta
    let nombrePuerta = '';
    // Revisar mapa en panelConfig.puertas
    if (es4Puertas && panelConfig?.puertas?.[`p4_d${puertaNum}`]?.nombre) {
        nombrePuerta = panelConfig.puertas[`p4_d${puertaNum}`].nombre;
    } else if (log.panelId === 'panel_1' && panelConfig?.puertas?.[`p1_d${puertaNum}`]?.nombre) {
        nombrePuerta = panelConfig.puertas[`p1_d${puertaNum}`].nombre;
    } else if (log.panelId === 'panel_2' && panelConfig?.puertas?.[`p2_d${puertaNum}`]?.nombre) {
        nombrePuerta = panelConfig.puertas[`p2_d${puertaNum}`].nombre;
    }

    // Si no vino de panelConfig pero log.puntoAcceso no es genérico (ej. "Acceso Principal", "Puerta Basura")
    if (!nombrePuerta && log.puntoAcceso && !log.puntoAcceso.match(/^Puerta\s+\d+$/i)) {
        nombrePuerta = log.puntoAcceso;
    }

    // Fallbacks descriptivos si aún no hay nombre
    if (!nombrePuerta) {
        if (esBasura) {
            nombrePuerta = 'Cuarto de Basura';
        } else if (es4Puertas) {
            nombrePuerta = puertaNum === 1 ? 'Puerta 1 (Acceso)' :
                           puertaNum === 2 ? 'Puerta 2 (Salida)' :
                           puertaNum === 3 ? 'Puerta 3 (Acceso 2)' : 'Puerta 4 (Salida 2)';
        } else {
            nombrePuerta = esSalida ? 'Pluma Salida' : 'Pluma Entrada';
        }
    }

    // 2. Determinar Nombre de Panel (Limpio, sin modelo)
    let nombrePanel = '';
    if (es4Puertas) {
        nombrePanel = panelConfig?.panel4pNombre || log.panelNombre || 'Panel 4 Puertas';
    } else if (log.panelId === 'panel_2' || esBasura) {
        nombrePanel = panelConfig?.panel2Nombre || log.panelNombre || 'Panel Cuarto Basura';
    } else {
        nombrePanel = panelConfig?.panel1Nombre || log.panelNombre || 'Panel Principal';
    }

    // Limpiar modelos entre paréntesis: (DS-K2814), (DS-K2812), (DS-K2811), (Hikvision...)
    const nombrePanelLimpio = String(nombrePanel)
        .replace(/\s*\((?:Hikvision\s*)?DS-[A-Z0-9-]+\)/gi, '')
        .replace(/\s*\([^)]*puertas?[^)]*\)/gi, '')
        .trim() || nombrePanel;

    // 3. Icono y Color
    let icono = '🟢';
    let color = '#15803d'; // Verde para entradas

    if (esBasura) {
        icono = '🗑️';
        color = '#7e22ce'; // Morado
    } else if (esSalida) {
        icono = '🟡';
        color = '#b45309'; // Ámbar para salidas
    } else if (puertaNum >= 3) {
        icono = '🔵';
        color = '#2563eb'; // Azul
    }

    return {
        nombrePuerta,
        nombrePanel: nombrePanelLimpio,
        textoCompleto: `${nombrePuerta} (${nombrePanelLimpio})`,
        icono,
        color
    };
};

const DepartamentosRFID = () => {
    const { fraccionamientoId } = useAuth();
    
    // Estados del Middleware y de la UI (Multi-Panel: DS-K2812 y DS-K2811)
    const [middlewareState, setMiddlewareState] = useState({
        online: false,
        panelConnected: false,
        panel1Connected: false,
        panel2Connected: false,
        panel4pConnected: false,
        panel1LastConnectedAt: null,
        panel2LastConnectedAt: null,
        panel4pLastConnectedAt: null,
        lastHeartbeat: null,
        panelTime: null
    });
    const [logs, setLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(true);
    const [refrescandoLogs, setRefrescandoLogs] = useState(false);
    const [comandoEjecutando, setComandoEjecutando] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [comandosRecientes, setComandosRecientes] = useState([]);
    const [loadingComandos, setLoadingComandos] = useState(true);
    const [usuariosPanel, setUsuariosPanel] = useState([]);
    const [inspeccionando, setInspeccionando] = useState(false);
    const [showInspeccionModal, setShowInspeccionModal] = useState(false);
    const [filtroBuscador, setFiltroBuscador] = useState('');

    // Estados para consulta de Tags registrados en Firebase (BD Cloud)
    const [tagsFirebase, setTagsFirebase] = useState([]);
    const [loadingTagsFirebase, setLoadingTagsFirebase] = useState(false);
    const [showTagsFirebaseModal, setShowTagsFirebaseModal] = useState(false);
    const [filtroBuscadorFirebase, setFiltroBuscadorFirebase] = useState('');

    // Estados para Filtrado del Historial de Comandos
    const [filtroCmdTexto, setFiltroCmdTexto] = useState('');
    const [filtroCmdEstatus, setFiltroCmdEstatus] = useState('todos');
    const [filtroCmdFechaInicio, setFiltroCmdFechaInicio] = useState('');
    const [filtroCmdFechaFin, setFiltroCmdFechaFin] = useState('');

    // Estados para Filtrado y Paginación del Historial de Accesos (Estética Zen)
    const [filtroTexto, setFiltroTexto] = useState('');
    const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
    const [filtroFechaFin, setFiltroFechaFin] = useState('');
    const [filtroHoraInicio, setFiltroHoraInicio] = useState('');
    const [filtroHoraFin, setFiltroHoraFin] = useState('');
    const [paginaActual, setPaginaActual] = useState(1);
    const registrosPorPagina = 10;

    // Resetear a la página 1 cuando cambie algún filtro
    useEffect(() => {
        setPaginaActual(1);
    }, [filtroTexto, filtroFechaInicio, filtroFechaFin, filtroHoraInicio, filtroHoraFin]);

    // Filtrar los logs en memoria (Estética Zen)
    const logsFiltrados = useMemo(() => {
        return logs.filter(log => {
            // 1. Filtro por Texto (NIP, residente, visitante, tag, QR, estado o casa)
            if (filtroTexto.trim() !== '') {
                const term = filtroTexto.toLowerCase().trim();
                const coincidenciaNombre = log.residenteNombre && log.residenteNombre.toLowerCase().includes(term);
                const coincidenciaCasa = log.numeroCasa && String(log.numeroCasa).toLowerCase().includes(term);
                const coincidenciaTag = log.rfid_tag && String(log.rfid_tag).toLowerCase().includes(term);
                const coincidenciaTipo = log.tipoAcceso && log.tipoAcceso.toLowerCase().includes(term);
                const coincidenciaPunto = log.puntoAcceso && log.puntoAcceso.toLowerCase().includes(term);
                const coincidenciaEstado = log.estadoAcceso && log.estadoAcceso.toLowerCase().includes(term);
                const coincidenciaMotivo = log.motivo && log.motivo.toLowerCase().includes(term);
                
                // Mapear QR en texto
                const esFiltroQr = term === 'qr' || term === 'invitado' || term === 'visita' || term === 'proveedor' || term === 'evento';
                const coincidenciaQr = esFiltroQr && log.tipoAcceso === 'qr';

                // Mapear NIP en texto
                const esFiltroNip = term === 'nip' || term === 'teclado';
                const coincidenciaNip = esFiltroNip && log.tipoAcceso === 'nip';
                
                // Mapear Tarjeta en texto
                const esFiltroTarjeta = term === 'tarjeta' || term === 'rfid' || term === 'tag' || term === 'uhf';
                const coincidenciaTarjeta = esFiltroTarjeta && log.tipoAcceso === 'tarjeta';
                
                // Mapear Rostro en texto
                const esFiltroRostro = term === 'rostro' || term === 'facial' || term === 'cara';
                const coincidenciaRostro = esFiltroRostro && log.tipoAcceso === 'rostro';

                // Mapear Moroso en texto
                const esFiltroMoroso = term === 'moroso' || term === 'deuda' || term === 'adeudo';
                const coincidenciaMoroso = esFiltroMoroso && log.estadoAcceso === 'denegado_moroso';

                if (!coincidenciaNombre && !coincidenciaCasa && !coincidenciaTag && !coincidenciaTipo && 
                    !coincidenciaPunto && !coincidenciaEstado && !coincidenciaMotivo && 
                    !coincidenciaQr && !coincidenciaNip && !coincidenciaTarjeta && !coincidenciaRostro && !coincidenciaMoroso) {
                    return false;
                }
            }

            // 2. Filtro por Rango de Fechas
            if (log.timestamp) {
                const logDate = new Date(log.timestamp);
                
                // Comparar sin horas para filtros de fecha completos
                if (filtroFechaInicio) {
                    const startLimit = new Date(filtroFechaInicio + 'T00:00:00');
                    if (logDate < startLimit) return false;
                }
                
                if (filtroFechaFin) {
                    const endLimit = new Date(filtroFechaFin + 'T23:59:59');
                    if (logDate > endLimit) return false;
                }

                // 3. Filtro por Rango de Horarios (De / A en horas/minutos del día)
                if (filtroHoraInicio || filtroHoraFin) {
                    const logMinutes = logDate.getHours() * 60 + logDate.getMinutes();
                    
                    if (filtroHoraInicio) {
                        const [startH, startM] = filtroHoraInicio.split(':').map(Number);
                        const startLimitMinutes = startH * 60 + startM;
                        if (logMinutes < startLimitMinutes) return false;
                    }
                    
                    if (filtroHoraFin) {
                        const [endH, endM] = filtroHoraFin.split(':').map(Number);
                        const endLimitMinutes = endH * 60 + endM;
                        if (logMinutes > endLimitMinutes) return false;
                    }
                }
            } else {
                // Si no tiene fecha y hay filtros temporales activos, lo omitimos
                if (filtroFechaInicio || filtroFechaFin || filtroHoraInicio || filtroHoraFin) {
                    return false;
                }
            }

            return true;
        });
    }, [logs, filtroTexto, filtroFechaInicio, filtroFechaFin, filtroHoraInicio, filtroHoraFin]);

    // Paginación de registros
    const logsPaginados = useMemo(() => {
        const indexInicio = (paginaActual - 1) * registrosPorPagina;
        return logsFiltrados.slice(indexInicio, indexInicio + registrosPorPagina);
    }, [logsFiltrados, paginaActual]);

    const totalPaginas = Math.ceil(logsFiltrados.length / registrosPorPagina) || 1;

    // Estados para la configuración física de los paneles (Panel 1 DS-K2812, Panel 2 DS-K2811 y Panel 3 DS-K2814)
    const [panelConfig, setPanelConfig] = useState({
        ip: '192.168.1.100',
        port: 8000,
        panel2_ip: '192.168.1.101',
        panel2_port: 8000,
        panel4p_ip: '192.168.1.118',
        panel4p_port: 8000,
        panel1Activo: true,
        panel2Activo: true,
        panel4pActivo: true,
        panel1Nombre: 'Panel Principal (Vehicular)',
        panel2Nombre: 'Panel Cuarto Basura',
        panel4pNombre: 'Panel 4 Puertas (DS-K2814)',
        syncActive: false,
        puertas: {}
    });
    const [ipInput, setIpInput] = useState('192.168.1.100');
    const [portInput, setPortInput] = useState('8000');
    const [panel2IpInput, setPanel2IpInput] = useState('192.168.1.101');
    const [panel2PortInput, setPanel2PortInput] = useState('8000');
    const [panel4pIpInput, setPanel4pIpInput] = useState('192.168.1.118');
    const [panel4pPortInput, setPanel4pPortInput] = useState('8000');
    const [guardandoConfig, setGuardandoConfig] = useState(false);
    const [panelRedSeleccionado, setPanelRedSeleccionado] = useState('panel4p');

    // Estados de visibilidad de secciones de diagnóstico
    const [showConsolaComandos, setShowConsolaComandos] = useState(false);

    // Estado de la información del fraccionamiento
    const [fraccionamientoInfo, setFraccionamientoInfo] = useState({ nombre: '', logoUrl: '' });

    // Cargar información del fraccionamiento para el reporte
    useEffect(() => {
        if (!fraccionamientoId) return;
        const fetchFraccData = async () => {
            try {
                const docRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const configRecibos = data.configuracionRecibos || {};
                    const logo = configRecibos.logoUrl || data.logoUrl || '';
                    setFraccionamientoInfo({
                        nombre: data.nombre || 'Fraccionamiento',
                        logoUrl: logo
                    });
                }
            } catch (err) {
                console.error('Error cargando información del fraccionamiento:', err);
            }
        };
        fetchFraccData();
    }, [fraccionamientoId]);

    // Escuchar la configuración del panel en tiempo real (panel_config)
    useEffect(() => {
        if (!fraccionamientoId) return;

        console.log('[Control Accesos] Iniciando escucha en tiempo real de panel_config...');
        const docRef = doc(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_config', 'panel_config');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setPanelConfig({
                    ip: data.panel1_ip || data.ip || '192.168.1.100',
                    port: data.panel1_port || data.port || 8000,
                    panel2_ip: data.panel2_ip || '192.168.1.101',
                    panel2_port: data.panel2_port || 8000,
                    panel4p_ip: data.panel4p_ip || data.panel3_ip || '192.168.1.118',
                    panel4p_port: data.panel4p_port || data.panel3_port || 8000,
                    panel1Activo: data.panel1Activo !== false,
                    panel2Activo: data.panel2Activo !== false,
                    panel4pActivo: data.panel4pActivo !== false,
                    panel1Nombre: data.panel1Nombre || 'Panel Principal (Vehicular)',
                    panel2Nombre: data.panel2Nombre || 'Panel Cuarto Basura',
                    panel4pNombre: data.panel4pNombre || 'Panel 4 Puertas (DS-K2814)',
                    syncActive: data.syncActive !== false,
                    puertas: data.puertas || {}
                });
                setIpInput(data.panel1_ip || data.ip || '192.168.1.100');
                setPortInput(String(data.panel1_port || data.port || 8000));
                setPanel2IpInput(data.panel2_ip || '192.168.1.101');
                setPanel2PortInput(String(data.panel2_port || 8000));
                setPanel4pIpInput(data.panel4p_ip || data.panel3_ip || '192.168.1.118');
                setPanel4pPortInput(String(data.panel4p_port || data.panel3_port || 8000));
            } else {
                console.warn('[Control Accesos] No existe el documento panel_config en Firestore.');
            }
        }, (err) => {
            console.error('[Control Accesos] Error en onSnapshot de panel_config:', err);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    const handleGuardarConfig = async (e) => {
        e.preventDefault();
        if (!fraccionamientoId) return;
        setGuardandoConfig(true);
        try {
            const docRef = doc(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_config', 'panel_config');
            await setDoc(docRef, {
                ip: ipInput.trim(),
                port: parseInt(portInput.trim(), 10) || 8000,
                panel1_ip: ipInput.trim(),
                panel1_port: parseInt(portInput.trim(), 10) || 8000,
                panel2_ip: panel2IpInput.trim(),
                panel2_port: parseInt(panel2PortInput.trim(), 10) || 8000,
                panel4p_ip: panel4pIpInput.trim(),
                panel4p_port: parseInt(panel4pPortInput.trim(), 10) || 8000
            }, { merge: true });
            
            setSuccessMessage('Configuración de los tres paneles guardada con éxito.');
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
            console.error('[Control Accesos] Error al guardar panel_config:', err);
            alert('Error al guardar la configuración de los paneles.');
        } finally {
            setGuardandoConfig(false);
        }
    };

    const handleToggleSync = async () => {
        if (!fraccionamientoId) return;
        try {
            const docRef = doc(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_config', 'panel_config');
            const nuevoEstado = !panelConfig.syncActive;
            
            await setDoc(docRef, {
                syncActive: nuevoEstado
            }, { merge: true });

            console.log('[Control Accesos] Sincronización establecida en:', nuevoEstado);
            setSuccessMessage(nuevoEstado ? 'Sincronización automática iniciada.' : 'Sincronización automática detenida.');
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
            console.error('[Control Accesos] Error al cambiar estado de sincronización:', err);
            alert('Error al cambiar el estado de la sincronización.');
        }
    };

    // Escuchar el estado de la Mini PC en tiempo real
    useEffect(() => {
        if (!fraccionamientoId) return;

        console.log('[Control Accesos] Iniciando escucha en tiempo real de middleware_state...');
        const docRef = doc(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_config', 'middleware_state');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                let isOnline = false;
                let lastHb = null;
                if (data.lastHeartbeat) {
                    lastHb = data.lastHeartbeat.toDate ? data.lastHeartbeat.toDate() : new Date(data.lastHeartbeat);
                    const diffMinutes = (new Date() - lastHb) / 60000;
                    isOnline = diffMinutes < 2; 
                }
                const panelLastConnected = data.panelLastConnectedAt
                    ? (data.panelLastConnectedAt.toDate ? data.panelLastConnectedAt.toDate() : new Date(data.panelLastConnectedAt))
                    : null;
                
                const panel1Connected = data.panel1Connected !== undefined ? Boolean(data.panel1Connected) : Boolean(data.panelConnected);
                const panel2Connected = Boolean(data.panel2Connected);
                const panel4pConnected = Boolean(data.panel4pConnected || data.panel3Connected);
                const panel1LastConnected = data.panel1LastConnectedAt
                    ? (data.panel1LastConnectedAt.toDate ? data.panel1LastConnectedAt.toDate() : new Date(data.panel1LastConnectedAt))
                    : panelLastConnected;
                const panel2LastConnected = data.panel2LastConnectedAt
                    ? (data.panel2LastConnectedAt.toDate ? data.panel2LastConnectedAt.toDate() : new Date(data.panel2LastConnectedAt))
                    : null;
                const panel4pLastConnected = (data.panel4pLastConnectedAt || data.panel3LastConnectedAt)
                    ? ((data.panel4pLastConnectedAt || data.panel3LastConnectedAt).toDate ? (data.panel4pLastConnectedAt || data.panel3LastConnectedAt).toDate() : new Date(data.panel4pLastConnectedAt || data.panel3LastConnectedAt))
                    : null;
                
                console.log('[Control Accesos] Middleware reporta:', {
                    online: isOnline,
                    panel1Connected,
                    panel2Connected,
                    panel4pConnected,
                    panel1LastConnected: panel1LastConnected ? panel1LastConnected.toLocaleString('es-MX') : 'Nunca',
                    panel2LastConnected: panel2LastConnected ? panel2LastConnected.toLocaleString('es-MX') : 'Nunca',
                    panel4pLastConnected: panel4pLastConnected ? panel4pLastConnected.toLocaleString('es-MX') : 'Nunca',
                    lastHeartbeat: lastHb ? lastHb.toLocaleTimeString('es-MX') : 'Ninguno'
                });
                setMiddlewareState({
                    online: isOnline,
                    panelConnected: panel1Connected || panel2Connected || panel4pConnected,
                    panel1Connected,
                    panel2Connected,
                    panel4pConnected,
                    panelLastConnectedAt: panel4pLastConnected || panel1LastConnected || panelLastConnected,
                    panel1LastConnectedAt: panel1LastConnected,
                    panel2LastConnectedAt: panel2LastConnected,
                    panel4pLastConnectedAt: panel4pLastConnected,
                    lastHeartbeat: lastHb,
                    panelTime: data.panelTime || null
                });
            } else {
                console.warn('[Control Accesos] No existe el documento middleware_state en Firestore.');
                setMiddlewareState({
                    online: false,
                    panelConnected: false,
                    panel1Connected: false,
                    panel2Connected: false,
                    panel4pConnected: false,
                    panelLastConnectedAt: null,
                    panel1LastConnectedAt: null,
                    panel2LastConnectedAt: null,
                    panel4pLastConnectedAt: null,
                    lastHeartbeat: null,
                    panelTime: null
                });
            }
        }, (err) => {
            console.error('[Control Accesos] Error en onSnapshot de middleware_state:', err);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Escuchar el historial de accesos en tiempo real
    useEffect(() => {
        if (!fraccionamientoId) return;

        console.log('[Control Accesos] Iniciando escucha en tiempo real de historial_accesos...');
        const colRef = collection(db, 'fraccionamientos', fraccionamientoId, 'historial_accesos');
        const q = query(colRef, orderBy('timestamp', 'desc'), limit(200));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const dataLogs = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                dataLogs.push({
                    id: doc.id,
                    ...data,
                    timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp)
                });
            });
            console.log(`[Control Accesos] Cargados ${dataLogs.length} logs de accesos recientes desde Firestore.`);
            setLogs(dataLogs);
            setLoadingLogs(false);
        }, (err) => {
            console.error('[Control Accesos] Error en onSnapshot de historial_accesos:', err);
            setLoadingLogs(false);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Función para refrescar manualmente o tras reconexión el historial de accesos
    const handleRefrescarHistorial = async () => {
        if (!fraccionamientoId) return;
        setRefrescandoLogs(true);
        try {
            console.log('[Control Accesos] Refrescando historial de accesos desde Firestore...');
            const colRef = collection(db, 'fraccionamientos', fraccionamientoId, 'historial_accesos');
            const q = query(colRef, orderBy('timestamp', 'desc'), limit(200));
            const snapshot = await getDocs(q);
            const dataLogs = [];
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                dataLogs.push({
                    id: docSnap.id,
                    ...data,
                    timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp)
                });
            });
            console.log(`[Control Accesos] Historial actualizado manualmente: ${dataLogs.length} registros cargados.`);
            setLogs(dataLogs);
        } catch (err) {
            console.warn('[Control Accesos] Error al refrescar historial de accesos:', err);
        } finally {
            setRefrescandoLogs(false);
        }
    };

    // Auto-refrescar historial cuando el navegador detecta que volvió la conexión a internet
    useEffect(() => {
        const handleOnline = () => {
            console.log('[Control Accesos] Red del navegador restablecida. Sincronizando historial...');
            handleRefrescarHistorial();
        };
        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, [fraccionamientoId]);

    // Escuchar comandos recientes en tiempo real para diagnóstico
    useEffect(() => {
        if (!fraccionamientoId) return;

        console.log('[Control Accesos] Iniciando escucha en tiempo real de control_accesos_comandos...');
        const colRef = collection(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_comandos');
        const q = query(colRef, orderBy('creadoEn', 'desc'), limit(100));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const dataCmds = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                dataCmds.push({
                    id: doc.id,
                    ...data,
                    creadoEn: data.creadoEn?.toDate ? data.creadoEn.toDate() : (data.creadoEn ? new Date(data.creadoEn) : null),
                    ejecutadoEn: data.ejecutadoEn?.toDate ? data.ejecutadoEn.toDate() : (data.ejecutadoEn ? new Date(data.ejecutadoEn) : null)
                });
            });
            console.log(`[Control Accesos] Cargados ${dataCmds.length} comandos recientes desde Firestore.`);
            setComandosRecientes(dataCmds);
            setLoadingComandos(false);
        }, (err) => {
            console.error('[Control Accesos] Error en onSnapshot de control_accesos_comandos:', err);
            setLoadingComandos(false);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Filtrar comandos en memoria según los criterios seleccionados
    const comandosFiltrados = useMemo(() => {
        return comandosRecientes.filter((cmd) => {
            // Filtro por estatus
            if (filtroCmdEstatus && filtroCmdEstatus !== 'todos') {
                if (cmd.estatus !== filtroCmdEstatus) return false;
            }
            // Filtro por texto
            if (filtroCmdTexto.trim() !== '') {
                const term = filtroCmdTexto.toLowerCase().trim();
                const coincideCmd = String(cmd.comando || '').toLowerCase().includes(term);
                const coincideDetalle = String(cmd.detalle || cmd.errorMsg || '').toLowerCase().includes(term);
                const coincidePanel = String(cmd.parametros?.panelId || '').toLowerCase().includes(term);
                const coincideId = String(cmd.id || '').toLowerCase().includes(term);
                if (!coincideCmd && !coincideDetalle && !coincidePanel && !coincideId) return false;
            }
            // Filtro por fecha inicio (CST)
            if (filtroCmdFechaInicio && cmd.creadoEn) {
                const fCmd = new Date(cmd.creadoEn);
                const fIni = new Date(filtroCmdFechaInicio + 'T00:00:00');
                if (fCmd < fIni) return false;
            }
            // Filtro por fecha fin (CST)
            if (filtroCmdFechaFin && cmd.creadoEn) {
                const fCmd = new Date(cmd.creadoEn);
                const fFin = new Date(filtroCmdFechaFin + 'T23:59:59');
                if (fCmd > fFin) return false;
            }
            return true;
        });
    }, [comandosRecientes, filtroCmdTexto, filtroCmdEstatus, filtroCmdFechaInicio, filtroCmdFechaFin]);

    // Exportar el historial de comandos filtrados a formato CSV
    const handleExportarComandosCsv = () => {
        if (!comandosFiltrados || comandosFiltrados.length === 0) {
            alert('No hay comandos en el historial para exportar con los filtros seleccionados.');
            return;
        }
        const columnas = [
            { label: 'ID Comando', key: 'id' },
            { label: 'Comando', key: 'nombreComando' },
            { label: 'Panel', key: 'panel' },
            { label: 'Puerta/Relé', key: 'puerta' },
            { label: 'Estatus', key: 'estatus' },
            { label: 'Fecha y Hora (CST)', key: 'fechaHora' },
            { label: 'Detalle / Observaciones del Servidor', key: 'detalle' }
        ];
        const datosExportar = comandosFiltrados.map((c) => ({
            id: c.id,
            nombreComando: c.comando,
            panel: c.parametros?.panelId === 'panel_2' ? 'Panel 2 (Basura)' : (c.parametros?.panelId === 'panel_1' ? 'Panel 1 (Vehicular)' : 'General'),
            puerta: c.parametros?.puertaId ? `R${c.parametros.puertaId}` : 'N/A',
            estatus: (c.estatus || '').toUpperCase(),
            fechaHora: c.creadoEn ? c.creadoEn.toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : 'N/A',
            detalle: c.errorMsg || c.detalle || 'Ejecutado exitosamente'
        }));

        const csvContent = toCsv(datosExportar, columnas);
        const fechaStr = new Date().toISOString().split('T')[0];
        downloadCsv(csvContent, `historial_comandos_hikvision_${fechaStr}.csv`);
    };

    // Consultar directamente los tags registrados en Firebase (BD Cloud) para comparación
    const handleConsultarTagsFirebase = async () => {
        if (!fraccionamientoId) return;
        setLoadingTagsFirebase(true);
        setShowTagsFirebaseModal(true);
        try {
            console.log('[Control Accesos] Consultando tags registrados en Firebase...');
            const TAG_KEYS = ['tag', 'tag2', 'tag3', 'tag4', 'tag5', 'tag6', 'tag7', 'tag8', 'tag9'];
            
            // Cargar configuración financiera para evaluar morosidad con meses de gracia
            let cuotaMensual = 0;
            let mesesPermitidos = 1;
            try {
                const fracDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (fracDoc.exists()) {
                    const cfg = fracDoc.data()?.configuracionFinanciera || {};
                    cuotaMensual = Number(cfg.cuotaMensual || cfg.montoMensual || 0);
                    mesesPermitidos = cfg.interesesMesesPermitidosDeuda !== undefined
                        ? Number(cfg.interesesMesesPermitidosDeuda)
                        : (cfg.mesesPermitidos !== undefined ? Number(cfg.mesesPermitidos) : 1);
                }
            } catch (cfgErr) {
                console.error('[Control Accesos] Error cargando configuración financiera:', cfgErr);
            }

            const casasSnap = await getDocs(collection(db, 'fraccionamientos', fraccionamientoId, 'casas'));
            const lista = [];

            casasSnap.docs.forEach((cDoc) => {
                const cData = cDoc.data();
                const numCasa = cData.numero || cData.numeroCasa || cData.lote || cDoc.id;
                const nombreTitular = cData.nombre_residente || cData.nombre || cData.nombreTitular || cData.nombreCompleto || cData.propietario || 'Residente';
                
                // Determinar estado financiero moroso respetando meses de gracia y convenios
                const adeudoCasa = Number(cData.adeudo || 0);
                const esMorosoFinanciero = cData.convenioActivo === true 
                    ? false 
                    : (typeof cData.moroso === 'boolean'
                        ? cData.moroso 
                        : calcularEstadoMoroso(adeudoCasa, cuotaMensual, mesesPermitidos));
                
                const casaBloqueadaAdmin = Boolean(cData.bloqueado);

                TAG_KEYS.forEach((tk, idx) => {
                    const val = cData[tk];
                    if (val && String(val).trim() !== '') {
                        const tagVal = String(val).trim();
                        const tagImpreso = convertirTagAntenaAImpreso(tagVal);
                        lista.push({
                            id: `${cDoc.id}_${tk}`,
                            casaId: cDoc.id,
                            numeroCasa: String(numCasa),
                            titular: nombreTitular,
                            tipoEtiqueta: `Tag ${idx + 1} (Titular)`,
                            tagAntena: tagVal,
                            tagImpreso: tagImpreso,
                            adeudo: esMorosoFinanciero,
                            montoAdeudo: adeudoCasa,
                            bloqueado: casaBloqueadaAdmin,
                            origen: 'Titular'
                        });
                    }
                });

                if (Array.isArray(cData.residentes)) {
                    cData.residentes.forEach((res, idx) => {
                        const rTag = res.tagRfid || res.rfid || res.tag;
                        if (rTag && String(rTag).trim() !== '') {
                            const tagVal = String(rTag).trim();
                            const tagImpreso = convertirTagAntenaAImpreso(tagVal);
                            lista.push({
                                id: `${cDoc.id}_res_${idx}`,
                                casaId: cDoc.id,
                                numeroCasa: String(numCasa),
                                titular: res.nombre || nombreTitular,
                                tipoEtiqueta: `Residente (${res.parentesco || 'Adicional'})`,
                                tagAntena: tagVal,
                                tagImpreso: tagImpreso,
                                adeudo: esMorosoFinanciero,
                                montoAdeudo: adeudoCasa,
                                bloqueado: casaBloqueadaAdmin || Boolean(res.bloqueado),
                                origen: 'Residente Adicional'
                            });
                        }
                    });
                }
            });

            lista.sort((a, b) => {
                const numA = parseInt(a.numeroCasa, 10);
                const numB = parseInt(b.numeroCasa, 10);
                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                return String(a.numeroCasa).localeCompare(String(b.numeroCasa));
            });

            console.log(`[Control Accesos] Se encontraron ${lista.length} tags registrados en Firebase.`);
            setTagsFirebase(lista);
        } catch (err) {
            console.error('[Control Accesos] Error al consultar tags de Firebase:', err);
            alert('Error al consultar tags de Firebase: ' + (err.message || 'Error desconocido'));
        } finally {
            setLoadingTagsFirebase(false);
        }
    };

    // Exportar la lista de tags en Firebase a formato CSV
    const handleExportarTagsFirebaseCsv = () => {
        if (!tagsFirebase || tagsFirebase.length === 0) {
            alert('No hay tags para exportar.');
            return;
        }
        const columnas = [
            { label: 'Casa / Depto', key: 'numeroCasa' },
            { label: 'Residente / Titular', key: 'titular' },
            { label: 'Tipo Asignación', key: 'tipoEtiqueta' },
            { label: 'Tag Antena (Decimal)', key: 'tagAntena' },
            { label: 'Tag Impreso', key: 'tagImpreso' },
            { label: 'Estado Financiero', key: 'estadoFinanciero' },
            { label: 'Estatus en Panel Físico', key: 'estatusPanel' }
        ];
        const datosExportar = tagsFirebase.map((t) => ({
            numeroCasa: t.numeroCasa,
            titular: t.titular,
            tipoEtiqueta: t.tipoEtiqueta,
            tagAntena: t.tagAntena,
            tagImpreso: t.tagImpreso || 'N/A',
            estadoFinanciero: t.adeudo ? 'Con Adeudo / Moroso' : 'Al Corriente',
            estatusPanel: (!t.adeudo && !t.bloqueado) ? 'AUTORIZADO (Grabado en Panel)' : 'BLOQUEADO (Retirado del Panel)'
        }));
        const fechaStr = new Date().toISOString().split('T')[0];
        downloadCsv(toCsv(datosExportar, columnas), `tags_firebase_cloud_${fechaStr}.csv`);
    };

    // Exportar la lista de residentes y tags leídos del panel físico a formato CSV
    const handleExportarPanelFisicoCsv = () => {
        if (!usuariosPanel || usuariosPanel.length === 0) {
            alert('No hay datos del panel físico para exportar.');
            return;
        }
        const columnas = [
            { label: 'ID Residente', key: 'userId' },
            { label: 'Nombre Residente', key: 'name' },
            { label: 'Tarjeta RFID (Decimal)', key: 'cardno' },
            { label: 'Tarjeta RFID (Impreso)', key: 'cardnoImpreso' },
            { label: 'NIP / Clave', key: 'password' },
            { label: 'Privilegio', key: 'rol' },
            { label: 'Grupo de Acceso', key: 'group' },
            { label: 'Horarios [TZ]', key: 'tz' },
            { label: 'Origen Datos', key: 'origen' }
        ];
        const datosExportar = usuariosPanel.map((u) => {
            const impreso = u.cardno && u.cardno !== 'Sin Tarjeta' && u.cardno !== 0 && u.cardno !== '0'
                ? convertirTagAntenaAImpreso(u.cardno)
                : 'N/A';
            return {
                userId: u.userId || 'S/N',
                name: u.name || 'Sin registro',
                cardno: u.cardno || 'Sin Tarjeta',
                cardnoImpreso: impreso,
                password: u.password || 'Ninguno',
                rol: u.role === 14 ? 'Administrador' : 'Usuario Normal',
                group: u.group !== undefined ? u.group : 1,
                tz: `[${u.tz1 || 1}, ${u.tz2 || 4}, ${u.tz3 || 0}]`,
                origen: u.origen === 'hardware' ? 'Memoria Física Hardware' : (u.origen === 'cache_offline' ? 'Caché Local Encriptada' : 'Middleware')
            };
        });
        const fechaStr = new Date().toISOString().split('T')[0];
        downloadCsv(toCsv(datosExportar, columnas), `memoria_panel_fisico_${fechaStr}.csv`);
    };



    // Función para obtener los usuarios grabados en la memoria física del panel (Lupa) vía Firestore
    const handleInspeccionarPanel = async () => {
        if (!fraccionamientoId) return;
        setInspeccionando(true);
        try {
            console.log('[Control Accesos] Enviando solicitud de usuarios al panel físico vía Firestore...');
            const colRef = collection(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_comandos');
            const docRef = await addDoc(colRef, {
                comando: 'solicitar_usuarios',
                parametros: {},
                estatus: 'pendiente',
                creadoEn: serverTimestamp()
            });

            // Escuchar la respuesta del middleware local en Firestore
            const unsub = onSnapshot(docRef, async (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.estatus === 'completado') {
                        let usuarios = (data.resultado && data.resultado.usuarios) || [];
                        
                        // Solo mostrar lo reportado honestamente por el hardware y el middleware de caseta
                        console.log(`[Control Accesos] Registros recibidos desde memoria de caseta: ${usuarios.length}`);

                        console.log(`[Control Accesos] Se obtuvieron ${usuarios.length} registros para visualización.`);
                        registerMovement({
                            fraccionamientoId,
                            titulo: 'Inspección de memoria física',
                            descripcion: `El administrador inspeccionó los registros del panel (Lupa). Se obtuvieron ${usuarios.length} registros.`,
                            categoria: 'Dispositivos',
                            tipoMovimiento: 'consulta',
                            origen: 'sistema'
                        }).catch(err => console.error('[Audit] Error al registrar movimiento:', err));

                        setUsuariosPanel(usuarios);
                        setShowInspeccionModal(true);
                        setInspeccionando(false);
                        unsub();
                    } else if (data.estatus === 'error') {
                        alert(`No se pudo leer la memoria del panel: ${data.errorMsg || 'Error desconocido'}`);
                        setInspeccionando(false);
                        unsub();
                    }
                }
            });
        } catch (err) {
            console.error('[Control Accesos] Error al solicitar lista de usuarios:', err);
            alert('Error de conexión: No se pudo registrar la solicitud en la base de datos.');
            setInspeccionando(false);
        }
    };

    // Enviar comandos al Middleware Local
    const handleEnviarComando = async (comando, parametros = {}) => {
        if (!fraccionamientoId) return;
        if (!middlewareState.online) {
            alert('El servidor local de caseta se encuentra OFFLINE. Asegúrate de que el middleware esté en ejecución en la computadora de caseta para enviar comandos remotos.');
            return;
        }

        if (comando === 'abrir_puerta') {
            const pId = parametros.panelId || 'panel_1';
            if (pId === 'panel_2' && !middlewareState.panel2Connected) {
                alert('No es posible enviar el pulso: El Panel 2 (Cuarto Basura) está desconectado físicamente en caseta.');
                return;
            }
            if (pId === 'panel_1' && !middlewareState.panel1Connected) {
                alert('No es posible enviar el pulso: El Panel 1 (Vehicular) está desconectado físicamente en caseta.');
                return;
            }
            if ((pId === 'panel_4puertas' || pId === 'panel_3') && !middlewareState.panel4pConnected) {
                alert('No es posible enviar el pulso: El Panel 3 (4 Puertas) está desconectado físicamente en caseta.');
                return;
            }
        }

        console.log(`[Control Accesos] Preparando envío de comando: "${comando}" con parámetros:`, parametros);
        setComandoEjecutando(comando);
        setSuccessMessage('');

        let timeoutId = null;

        try {
            const colRef = collection(db, 'fraccionamientos', fraccionamientoId, 'control_accesos_comandos');
            const docRef = await addDoc(colRef, {
                comando,
                parametros,
                estatus: 'pendiente',
                creadoEn: serverTimestamp()
            });
            console.log(`[Control Accesos] Documento de comando "${comando}" creado con ID:`, docRef.id);

            // Escuchar la resolución del comando
            const unsub = onSnapshot(docRef, (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    console.log(`[Control Accesos] Cambio de estatus del comando ${docRef.id}:`, data.estatus);
                    if (data.estatus === 'completado') {
                        if (timeoutId) clearTimeout(timeoutId);
                        console.log(`[Control Accesos] Comando ${docRef.id} completado con éxito.`);
                        
                        let tituloMov = 'Comando ejecutado';
                        let descMov = `El administrador ejecutó el comando "${comando}" en el panel.`;
                        let tipoMov = 'movimiento';

                        if (comando === 'sincronizar_todo') {
                            tituloMov = 'Sincronización general de base de datos';
                            descMov = 'El administrador inició la sincronización general de todos los residentes con el panel físico.';
                            tipoMov = 'sincronizacion';
                        } else if (comando === 'limpiar_memoria') {
                            tituloMov = 'Limpieza general de memoria física';
                            descMov = 'El administrador purgó por completo la memoria física de usuarios y logs del panel.';
                            tipoMov = 'eliminacion';
                        } else if (comando === 'abrir_puerta') {
                            const pId = parametros.puertaId || 1;
                            const esPanel2 = parametros.panelId === 'panel_2';
                            if (esPanel2) {
                                tituloMov = `Apertura remota Cuarto de Basura (Panel 2 - Relevador ${pId})`;
                                descMov = `El administrador envió una señal de apertura remota a la puerta del Cuarto de Basura (Panel 2 DS-K2811, Relevador ${pId}).`;
                            } else {
                                tituloMov = `Apertura remota de pluma (${pId === 2 ? 'Salida' : 'Entrada'} - Panel 1)`;
                                descMov = `El administrador envió una señal de apertura remota de la pluma de ${pId === 2 ? 'salida (Relevador 2)' : 'entrada (Relevador 1)'} en Panel 1 DS-K2812.`;
                            }
                            tipoMov = 'apertura';
                        }

                        registerMovement({
                            fraccionamientoId,
                            titulo: tituloMov,
                            descripcion: descMov,
                            categoria: 'Dispositivos',
                            tipoMovimiento: tipoMov,
                            origen: 'sistema'
                        }).catch(err => console.error('[Audit] Error al registrar movimiento:', err));

                        setComandoEjecutando(null);
                        setSuccessMessage(`Comando "${comando}" ejecutado exitosamente en el panel.`);
                        unsub();
                        setTimeout(() => setSuccessMessage(''), 5000);
                    } else if (data.estatus === 'error') {
                        if (timeoutId) clearTimeout(timeoutId);
                        console.error(`[Control Accesos] Comando ${docRef.id} falló:`, data.errorMsg);
                        setComandoEjecutando(null);
                        alert(`Error al ejecutar comando: ${data.errorMsg || 'Error desconocido en el servidor local'}`);
                        unsub();
                    }
                }
            });

            // Timeout de seguridad para evitar que los botones queden bloqueados en "Abriendo..."
            timeoutId = setTimeout(() => {
                unsub();
                setComandoEjecutando(null);
                alert(`Tiempo de espera agotado: El panel de caseta no respondió a la instrucción en 15 segundos.`);
            }, 15000);
        } catch (err) {
            if (timeoutId) clearTimeout(timeoutId);
            console.error('[Control Accesos] Error al registrar comando en Firestore:', err);
            setComandoEjecutando(null);
            alert('Error al establecer conexión con Firestore para enviar el comando.');
        }
    };

    // Función para borrar el historial de logs de acceso por lotes seguros (<500 documentos por batch)
    const handleBorrarHistorialLogs = async () => {
        if (!fraccionamientoId) return;
        if (!window.confirm('¿Estás seguro de que deseas borrar todo el historial de accesos del fraccionamiento? Esta acción limpiará la tabla de registros recientes de caseta.')) {
            return;
        }
        console.log('[Control Accesos] Iniciando borrado del historial de accesos por lotes seguros en Firestore...');
        try {
            const colRef = collection(db, 'fraccionamientos', fraccionamientoId, 'historial_accesos');
            const LOTE_TAMANO = 400; // Margen de seguridad sobre el límite de 500 de Firestore
            let totalBorrados = 0;

            while (true) {
                const q = query(colRef, limit(LOTE_TAMANO));
                const snap = await getDocs(q);

                if (snap.empty) {
                    break;
                }

                const batch = writeBatch(db);
                snap.docs.forEach((docSnap) => {
                    batch.delete(docSnap.ref);
                });
                await batch.commit();

                totalBorrados += snap.size;
                console.log(`[Control Accesos] Eliminados ${totalBorrados} registros acumulados...`);

                if (snap.size < LOTE_TAMANO) {
                    break;
                }
            }

            if (totalBorrados === 0) {
                alert('No hay registros de acceso para eliminar.');
                return;
            }

            console.log(`[Control Accesos] Historial de accesos borrado exitosamente (${totalBorrados} registros eliminados).`);
            registerMovement({
                fraccionamientoId,
                titulo: 'Borrado de historial de accesos',
                descripcion: `El administrador eliminó por completo el historial de registros de accesos recientes de la base de datos (${totalBorrados} registros purgados).`,
                categoria: 'Dispositivos',
                tipoMovimiento: 'eliminacion',
                origen: 'sistema'
            }).catch(err => console.error('[Audit] Error al registrar movimiento:', err));

            setSuccessMessage(`Historial de accesos borrado con éxito (${totalBorrados} registros eliminados).`);
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
            console.error('[Control Accesos] Error al eliminar historial de accesos:', err);
            alert('Ocurrió un error al intentar eliminar el historial de accesos: ' + (err.message || 'Error desconocido'));
        }
    };

    const pickFirstValue = (obj, keys) => {
        for (const k of keys) {
            const v = obj?.[k];
            if (v !== undefined && v !== null) return v;
        }
        return null;
    };

    const resolveResidenteNombre = (casa) => {
        const name = pickFirstValue(casa, [
            'nombre_residente', 'nombre', 'nombreTitular', 'titular', 'propietario', 'arrendatario', 'nombrePropietario', 'nombreArrendatario', 'nombreCompleto'
        ]);
        return (typeof name === 'string' && name.trim().length) ? name.trim() : 'Sin registro';
    };

    const resolveCasaLabel = (casa) => {
        const numero = pickFirstValue(casa, ['numero', 'numeroCasa', 'lote', 'houseLabel', 'numeroLote']);
        return typeof numero === 'string' || typeof numero === 'number' ? String(numero) : '';
    };

    const handleRFIDExport = async () => {
        if (!fraccionamientoId) {
            alert('Selecciona un fraccionamiento para generar el listado.');
            return;
        }

        let financialConfig = {};
        try {
            const fracRef = doc(db, 'fraccionamientos', fraccionamientoId);
            const fracSnap = await getDoc(fracRef);
            if (fracSnap.exists()) {
                const data = fracSnap.data();
                const config = data.configuracionFinanciera || {};
                financialConfig = {
                    mesesPermitidos: config.interesesMesesPermitidosDeuda || config.mesesPermitidos || 1,
                    cuotaMensual: config.cuotaMensual || config.montoMensual || 0,
                };
            }
        } catch (err) {
            console.error('Error cargando configuración financiera:', err);
        }

        const ref = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const q = query(ref, orderBy('numero'));
        const snap = await getDocs(q);

        const TAG_KEYS = ['tag', 'tag2', 'tag3']; // Solo mapeamos los 3 primeros tags de acuerdo a la decisión

        const rows = [];
        snap.forEach((doc) => {
            const casa = { id: doc.id, ...doc.data() };
            const adeudo = calcularAdeudoVisibleDesdeObjeto(casa, financialConfig);
            const cuotaMensual = Number(financialConfig.cuotaMensual || 0);
            const mesesPermitidos = Number(financialConfig.mesesPermitidos || 1);
            const esMoroso = calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos);
            if (!esMoroso) return;

            const row = {
                casa: resolveCasaLabel(casa),
                residente: resolveResidenteNombre(casa),
                montoAdeudo: adeudo,
            };

            TAG_KEYS.forEach((tagKey, index) => {
                const tagValue = casa[tagKey];
                row[`tag_${index}`] = tagValue && String(tagValue).trim() !== '' ? String(tagValue).trim() : '';
            });

            rows.push(row);
        });

        const headers = [
            { key: 'casa', label: 'Casa/Departamento' },
            { key: 'residente', label: 'Residente' },
            { key: 'montoAdeudo', label: 'Monto de deuda' },
        ];

        TAG_KEYS.forEach((tagKey, index) => {
            headers.push({ key: `tag_${index}`, label: `Tag ${index + 1}` });
        });

        const csv = toCsv(rows, headers);
        downloadCsv(csv, 'morosos_rfid.csv');
    };

    const handleExportarLogsCSV = () => {
        if (logsFiltrados.length === 0) {
            alert('No hay registros para exportar.');
            return;
        }

        const sanitizedNombre = (fraccionamientoInfo.nombre || '').replace(/"/g, '""');
        const metadata = [
            `"Fraccionamiento:","${sanitizedNombre}"`,
            `"Reporte:","Historial de Accesos Recientes en Caseta"`,
            `"Fecha de Exportación:","${new Date().toLocaleString('es-MX')}"`,
            `""`
        ].join('\n');

        const rows = logsFiltrados.map(log => {
            let tipoLabel = 'Tag RFID';
            if (log.tipoAcceso === 'qr') tipoLabel = 'Código QR';
            else if (log.tipoAcceso === 'nip') tipoLabel = 'NIP';
            else if (log.tipoAcceso === 'rostro') tipoLabel = 'Rostro';

            let estadoLabel = 'Concedido';
            if (log.estadoAcceso === 'denegado_moroso') estadoLabel = 'Denegado (Moroso)';
            else if (log.estadoAcceso === 'denegado_regla_oro') estadoLabel = 'Denegado (Regla de Oro - Sin Entrada)';
            else if (log.estadoAcceso === 'denegado_expirado') estadoLabel = 'Denegado (Expirado)';
            else if (log.estadoAcceso === 'denegado_cupo') estadoLabel = 'Denegado (Cupo Agotado)';
            else if (log.estadoAcceso === 'denegado_no_registrado') estadoLabel = 'Denegado (No Registrado)';
            else if (log.estadoAcceso && log.estadoAcceso.startsWith('denegado')) estadoLabel = 'Denegado';

            const puntoInfo = resolverPuntoAccesoInfo(log, panelConfig);

            return {
                fechaHora: log.timestamp ? log.timestamp.toLocaleString('es-MX') : '',
                casaLote: log.numeroCasa || '',
                residente: log.residenteNombre || '',
                tagRfid: log.rfid_tag || '',
                puntoAcceso: puntoInfo.textoCompleto,
                tipo: tipoLabel,
                estado: estadoLabel,
                motivo: log.motivo || ''
            };
        });

        const headers = [
            { key: 'fechaHora', label: 'Fecha y Hora' },
            { key: 'casaLote', label: 'Casa / Lote' },
            { key: 'residente', label: 'Residente / Visitante' },
            { key: 'tagRfid', label: 'Identificador (Tag/QR)' },
            { key: 'puntoAcceso', label: 'Punto de Acceso' },
            { key: 'tipo', label: 'Tipo de Acceso' },
            { key: 'estado', label: 'Estado' },
            { key: 'motivo', label: 'Motivo / Detalle' }
        ];

        const dataCsv = toCsv(rows, headers);
        const finalCsv = metadata + '\n' + dataCsv;

        downloadCsv(finalCsv, 'registros_accesos.csv');
    };

    return (
        <div className="departamentos-page">
            <div className="departamentos-container">
                <header className="departamentos-header">
                    <div className="departamentos-header-icon">
                        <FaBroadcastTower />
                    </div>
                    <div>
                        <h1>Control RFID de morosos</h1>
                        <p>Gestiona los accesos vehiculares y peatonales del fraccionamiento mediante hardware físico y sincronización con Firebase.</p>
                    </div>
                    <Link to="/administracion" className="departamentos-back-link">
                        <FaChevronLeft /> Regresar a administración
                    </Link>
                </header>

                {/* Dashboard del Middleware (Fila 1: Estado del Servidor y Acciones Físicas) */}
                <section className="departamentos-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px', alignItems: 'stretch' }}>
                    {/* Tarjeta de Estado del Servidor Local */}
                    <article className="departamentos-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box', marginTop: 0 }}>
                        <div>
                            <div className="departamentos-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '15px' }}>
                                <h2 className="section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><FaServer /> Estado del Servidor</h2>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: 'bold', padding: '4px 8px', borderRadius: '20px', backgroundColor: middlewareState.online ? '#e8f5e9' : '#ffebee', color: middlewareState.online ? '#2e7d32' : '#c62828' }}>
                                        <FaCircle className={middlewareState.online ? 'pulse-green' : ''} style={{ fontSize: '0.75rem' }} />
                                        {middlewareState.online ? 'ONLINE' : 'OFFLINE'}
                                    </span>
                                    <span style={{ fontSize: '0.75rem', color: '#757575', fontWeight: 'bold' }}>
                                        U.C. Panel: {middlewareState.panelLastConnectedAt ? middlewareState.panelLastConnectedAt.toLocaleString('es-MX') : 'Nunca'}
                                    </span>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                                {panelConfig.panel1Activo !== false && (
                                    <div style={{ padding: '10px', borderRadius: '8px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                                            <span style={{ fontSize: '0.72rem', color: '#6c757d', textTransform: 'uppercase', fontWeight: 'bold' }}>Panel 1 (Vehicular)</span>
                                            <span style={{ fontSize: '0.68rem', color: '#0288d1', fontWeight: 'bold' }}>DS-K2812</span>
                                        </div>
                                        <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: !middlewareState.online ? '#e65100' : (middlewareState.panel1Connected ? '#2e7d32' : '#c62828'), display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            {!middlewareState.online ? '⚠️ Sin Reporte' : (middlewareState.panel1Connected ? '✅ Conectado' : '❌ Desconectado')}
                                        </span>
                                        <span style={{ fontSize: '0.72rem', color: '#757575', display: 'block', marginTop: '2px' }}>
                                            {panelConfig.ip}:{panelConfig.port}
                                        </span>
                                    </div>
                                )}

                                {panelConfig.panel2Activo !== false && (
                                    <div style={{ padding: '10px', borderRadius: '8px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                                            <span style={{ fontSize: '0.72rem', color: '#6c757d', textTransform: 'uppercase', fontWeight: 'bold' }}>Panel 2 (Basura)</span>
                                            <span style={{ fontSize: '0.68rem', color: '#7b1fa2', fontWeight: 'bold' }}>DS-K2811</span>
                                        </div>
                                        <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: !middlewareState.online ? '#e65100' : (middlewareState.panel2Connected ? '#2e7d32' : '#c62828'), display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            {!middlewareState.online ? '⚠️ Sin Reporte' : (middlewareState.panel2Connected ? '✅ Conectado' : '❌ Desconectado')}
                                        </span>
                                        <span style={{ fontSize: '0.72rem', color: '#757575', display: 'block', marginTop: '2px' }}>
                                            {panelConfig.panel2_ip}:{panelConfig.panel2_port}
                                        </span>
                                    </div>
                                )}

                                {panelConfig.panel4pActivo !== false && (
                                    <div style={{ padding: '10px', borderRadius: '8px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                                            <span style={{ fontSize: '0.72rem', color: '#6c757d', textTransform: 'uppercase', fontWeight: 'bold' }}>Panel 3 (4 Puertas)</span>
                                            <span style={{ fontSize: '0.68rem', color: '#16a34a', fontWeight: 'bold' }}>DS-K2814</span>
                                        </div>
                                        <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: !middlewareState.online ? '#e65100' : (middlewareState.panel4pConnected ? '#2e7d32' : '#c62828'), display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            {!middlewareState.online ? '⚠️ Sin Reporte' : (middlewareState.panel4pConnected ? '✅ Conectado' : '❌ Desconectado')}
                                        </span>
                                        <span style={{ fontSize: '0.72rem', color: '#757575', display: 'block', marginTop: '2px' }}>
                                            {panelConfig.panel4p_ip}:{panelConfig.panel4p_port}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {middlewareState.online && middlewareState.panelTime && (
                                <div style={{ fontSize: '0.74rem', color: '#495057', marginBottom: '12px', padding: '5px 10px', backgroundColor: '#f1f8e9', borderRadius: '6px', border: '1px solid #dcedc8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>🕒 Reloj del Sistema: <strong style={{ color: '#2e7d32' }}>{middlewareState.panelTime}</strong></span>
                                    <span style={{ color: panelConfig.syncActive ? '#0288d1' : '#757575', fontWeight: 'bold' }}>
                                        {panelConfig.syncActive ? '⚡ Monitoreo Activo' : '⏸️ Pausado'}
                                    </span>
                                </div>
                            )}

                            {middlewareState.online && ((panelConfig.panel1Activo !== false && !middlewareState.panel1Connected) || (panelConfig.panel2Activo !== false && !middlewareState.panel2Connected) || (panelConfig.panel4pActivo !== false && !middlewareState.panel4pConnected)) && (
                                <div className="note-box" style={{ borderLeftColor: '#f44336', backgroundColor: '#ffebee', padding: '10px', borderRadius: '6px', marginBottom: '15px' }}>
                                    <h4 style={{ color: '#d32f2f', margin: '0 0 4px 0', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
                                        <FaExclamationTriangle /> Alerta de Conexión Física de Paneles:
                                    </h4>
                                    <ul style={{ margin: '2px 0 0 16px', padding: 0, fontSize: '0.72rem', color: '#555', lineHeight: '1.4' }}>
                                        {panelConfig.panel1Activo !== false && !middlewareState.panel1Connected && (
                                            <li><strong>Panel 1 (DS-K2812 Vehicular):</strong> Sin respuesta en {panelConfig.ip}:{panelConfig.port}.</li>
                                        )}
                                        {panelConfig.panel2Activo !== false && !middlewareState.panel2Connected && (
                                            <li><strong>Panel 2 (DS-K2811 Cuarto Basura):</strong> Sin respuesta en {panelConfig.panel2_ip}:{panelConfig.panel2_port}.</li>
                                        )}
                                        {panelConfig.panel4pActivo !== false && !middlewareState.panel4pConnected && (
                                            <li><strong>Panel 3 (DS-K2814 4 Puertas):</strong> Sin respuesta en {panelConfig.panel4p_ip}:{panelConfig.panel4p_port}.</li>
                                        )}
                                    </ul>
                                </div>
                            )}

                            {/* Controles de Sincronización Start/Stop */}
                            <div style={{ marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <span style={{ fontSize: '0.82rem', fontWeight: 'bold', color: '#495057' }}>Enlace Físico con Dispositivos:</span>
                                <button
                                    type="button"
                                    onClick={handleToggleSync}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        padding: '10px',
                                        borderRadius: '8px',
                                        fontWeight: 'bold',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        border: 'none',
                                        backgroundColor: panelConfig.syncActive ? '#f44336' : '#2e7d32',
                                        color: '#ffffff',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    {panelConfig.syncActive ? (
                                        <>
                                            <FaCircle style={{ color: '#fff', fontSize: '0.75rem' }} /> Desconectar Paneles de Control
                                        </>
                                    ) : (
                                        <>
                                            <FaSync style={{ color: '#fff', fontSize: '0.75rem' }} /> Conectar con Paneles de Control
                                        </>
                                    )}
                                </button>
                                <p style={{ margin: 0, fontSize: '0.72rem', color: '#6c757d', fontStyle: 'italic' }}>
                                    {panelConfig.syncActive 
                                        ? 'El middleware está enlazado concurrentemente con los paneles activos y monitorea los accesos en tiempo real.' 
                                        : 'Enlace en pausa. El middleware no procesará eventos de ninguno de los paneles.'}
                                </p>
                            </div>
                        </div>

                        {/* Formulario de Configuración de IP / Puerto para los paneles */}
                        <form onSubmit={handleGuardarConfig} style={{ borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                                <span style={{ fontSize: '0.84rem', fontWeight: 'bold', color: '#1e293b' }}>
                                    Configuración de Red por Panel:
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <label htmlFor="selector-panel-red" style={{ fontSize: '0.74rem', fontWeight: '600', color: '#64748b' }}>
                                        Configurar:
                                    </label>
                                    <select
                                        id="selector-panel-red"
                                        value={panelRedSeleccionado}
                                        onChange={(e) => setPanelRedSeleccionado(e.target.value)}
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.78rem',
                                            fontWeight: '600',
                                            color: '#0f172a',
                                            backgroundColor: '#fff',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {panelConfig.panel4pActivo !== false && (
                                            <option value="panel4p">🟢 Panel 3: {panelConfig.panel4pNombre || 'Hikvision DS-K2814 (4 Puertas)'}</option>
                                        )}
                                        {panelConfig.panel1Activo !== false && (
                                            <option value="panel1">🔵 Panel 1: {panelConfig.panel1Nombre || 'Hikvision DS-K2812 (Vehicular)'}</option>
                                        )}
                                        {panelConfig.panel2Activo !== false && (
                                            <option value="panel2">🟣 Panel 2: {panelConfig.panel2Nombre || 'Hikvision DS-K2811 (Basura)'}</option>
                                        )}
                                    </select>
                                </div>
                            </div>

                            {/* Tarjeta del Panel 1 (DS-K2812 - Azul) */}
                            {panelRedSeleccionado === 'panel1' && (
                                <div style={{ backgroundColor: '#eff6ff', padding: '12px 14px', borderRadius: '8px', border: '1px solid #bfdbfe', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 'bold', color: '#1d4ed8' }}>
                                            🔵 {panelConfig.panel1Nombre || 'Panel 1: Hikvision DS-K2812'}
                                        </span>
                                        <span style={{ fontSize: '0.72rem', background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                                            2 Puertas / 4 Lectores (Vehicular)
                                        </span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                                        <div>
                                            <label htmlFor="panel1-ip" style={{ fontSize: '0.74rem', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '3px' }}>Dirección IP</label>
                                            <input
                                                id="panel1-ip"
                                                type="text"
                                                value={ipInput}
                                                onChange={(e) => setIpInput(e.target.value.replace(/[^0-9.]/g, '').substring(0, 15))}
                                                placeholder="192.168.1.100"
                                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #93c5fd', fontSize: '0.85rem', boxSizing: 'border-box', background: '#fff' }}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="panel1-port" style={{ fontSize: '0.74rem', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '3px' }}>Puerto</label>
                                            <input
                                                id="panel1-port"
                                                type="text"
                                                value={portInput}
                                                onChange={(e) => setPortInput(e.target.value.replace(/[^0-9]/g, '').substring(0, 5))}
                                                placeholder="8000"
                                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #93c5fd', fontSize: '0.85rem', boxSizing: 'border-box', background: '#fff' }}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Tarjeta del Panel 2 (DS-K2811 - Púrpura) */}
                            {panelRedSeleccionado === 'panel2' && (
                                <div style={{ backgroundColor: '#faf5ff', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e9d5ff', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 'bold', color: '#7c3aed' }}>
                                            🟣 {panelConfig.panel2Nombre || 'Panel 2: Hikvision DS-K2811'}
                                        </span>
                                        <span style={{ fontSize: '0.72rem', background: '#f3e8ff', color: '#6b21a8', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                                            1 Puerta / 2 Lectores (Cuarto Basura)
                                        </span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                                        <div>
                                            <label htmlFor="panel2-ip" style={{ fontSize: '0.74rem', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '3px' }}>Dirección IP</label>
                                            <input
                                                id="panel2-ip"
                                                type="text"
                                                value={panel2IpInput}
                                                onChange={(e) => setPanel2IpInput(e.target.value.replace(/[^0-9.]/g, '').substring(0, 15))}
                                                placeholder="192.168.1.101"
                                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #c084fc', fontSize: '0.85rem', boxSizing: 'border-box', background: '#fff' }}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="panel2-port" style={{ fontSize: '0.74rem', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '3px' }}>Puerto</label>
                                            <input
                                                id="panel2-port"
                                                type="text"
                                                value={panel2PortInput}
                                                onChange={(e) => setPanel2PortInput(e.target.value.replace(/[^0-9]/g, '').substring(0, 5))}
                                                placeholder="8000"
                                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #c084fc', fontSize: '0.85rem', boxSizing: 'border-box', background: '#fff' }}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Tarjeta del Panel 3 (DS-K2814 - Verde Esmeralda) */}
                            {panelRedSeleccionado === 'panel4p' && (
                                <div style={{ backgroundColor: '#ecfdf5', padding: '12px 14px', borderRadius: '8px', border: '1px solid #a7f3d0', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 'bold', color: '#059669' }}>
                                            🟢 {panelConfig.panel4pNombre || 'Panel 3: Hikvision DS-K2814'}
                                        </span>
                                        <span style={{ fontSize: '0.72rem', background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                                            4 Puertas / 8 Lectores (Industrial 192.168.1.118)
                                        </span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                                        <div>
                                            <label htmlFor="panel4p-ip" style={{ fontSize: '0.74rem', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '3px' }}>Dirección IP</label>
                                            <input
                                                id="panel4p-ip"
                                                type="text"
                                                value={panel4pIpInput}
                                                onChange={(e) => setPanel4pIpInput(e.target.value.replace(/[^0-9.]/g, '').substring(0, 15))}
                                                placeholder="192.168.1.118"
                                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #6ee7b7', fontSize: '0.85rem', boxSizing: 'border-box', background: '#fff' }}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="panel4p-port" style={{ fontSize: '0.74rem', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '3px' }}>Puerto</label>
                                            <input
                                                id="panel4p-port"
                                                type="text"
                                                value={panel4pPortInput}
                                                onChange={(e) => setPanel4pPortInput(e.target.value.replace(/[^0-9]/g, '').substring(0, 5))}
                                                placeholder="8000"
                                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #6ee7b7', fontSize: '0.85rem', boxSizing: 'border-box', background: '#fff' }}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={guardandoConfig}
                                style={{
                                    width: '100%',
                                    padding: '7px 16px',
                                    borderRadius: '6px',
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                    border: 'none',
                                    fontWeight: 'bold',
                                    fontSize: '0.82rem',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}
                            >
                                {guardandoConfig ? 'Guardando configuración...' : '💾 Guardar Configuración de Paneles'}
                            </button>
                        </form>
                    </article>

                    {/* Panel de Sincronización y Acciones Físicas */}
                    <article className="departamentos-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box', marginTop: 0 }}>
                        <div>
                            <div className="departamentos-card-header" style={{ borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '15px' }}>
                                <h2 className="section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><FaShieldAlt /> Sincronización y Acciones Físicas</h2>
                            </div>
                        <p className="help-text" style={{ marginBottom: '10px' }}>Envía instrucciones directas al Middleware en caseta para administrar la memoria física del panel.</p>
                        
                        <div className="rfid-box" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <button 
                                type="button" 
                                className="primary-button" 
                                disabled={comandoEjecutando !== null}
                                onClick={() => handleEnviarComando('sincronizar_todo')}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px' }}
                            >
                                <FaSync className={comandoEjecutando === 'sincronizar_todo' ? 'spin' : ''} />
                                {comandoEjecutando === 'sincronizar_todo' ? 'Sincronizando con BD...' : 'Iniciar Sincronización con BD'}
                            </button>

                            {/* Grupo 1: Aperturas Panel 1 (Vehicular DS-K2812) */}
                            {panelConfig.panel1Activo !== false && (() => {
                                const p1Offline = !middlewareState.online || !middlewareState.panel1Connected;
                                return (
                                <div>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: p1Offline ? '#dc2626' : '#0288d1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                        <span>🚗 Panel 1 ({panelConfig.panel1Nombre || 'Vehicular - DS-K2812'}):</span>
                                        {p1Offline && <span style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 'bold' }}>🔴 Desconectado</span>}
                                    </span>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                        <button 
                                            type="button" 
                                            disabled={comandoEjecutando !== null || p1Offline}
                                            onClick={() => handleEnviarComando('abrir_puerta', { panelId: 'panel_1', puertaId: 1 })}
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                gap: '6px', 
                                                border: `1px solid ${p1Offline ? '#cbd5e1' : '#2e7d32'}`, 
                                                color: p1Offline ? '#94a3b8' : '#2e7d32', 
                                                backgroundColor: p1Offline ? '#f8fafc' : '#e8f5e9', 
                                                padding: '8px 6px', 
                                                fontWeight: 'bold', 
                                                borderRadius: '6px', 
                                                cursor: (comandoEjecutando || p1Offline) ? 'not-allowed' : 'pointer', 
                                                fontSize: '0.8rem',
                                                opacity: p1Offline ? 0.6 : 1
                                            }}
                                            title={p1Offline ? 'Panel 1 Desconectado físicamente' : 'Abrir Pluma Entrada'}
                                        >
                                            <FaDoorOpen />
                                            {comandoEjecutando === 'abrir_puerta' ? 'Abriendo...' : (panelConfig.puertas?.p1_d1?.nombre || '🟢 Pluma Entrada (P1-R1)')}
                                            {p1Offline && <small style={{ fontSize: '0.68rem', color: '#dc2626' }}> (Offline)</small>}
                                            {panelConfig.puertas?.p1_d1?.accesoGeneral && <span title="Acceso General (Bypass Morosidad)">⭐</span>}
                                        </button>

                                        <button 
                                            type="button" 
                                            disabled={comandoEjecutando !== null || p1Offline}
                                            onClick={() => handleEnviarComando('abrir_puerta', { panelId: 'panel_1', puertaId: 2 })}
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                gap: '6px', 
                                                border: `1px solid ${p1Offline ? '#cbd5e1' : '#e65100'}`, 
                                                color: p1Offline ? '#94a3b8' : '#e65100', 
                                                backgroundColor: p1Offline ? '#f8fafc' : '#fff3e0', 
                                                padding: '8px 6px', 
                                                fontWeight: 'bold', 
                                                borderRadius: '6px', 
                                                cursor: (comandoEjecutando || p1Offline) ? 'not-allowed' : 'pointer', 
                                                fontSize: '0.8rem',
                                                opacity: p1Offline ? 0.6 : 1
                                            }}
                                            title={p1Offline ? 'Panel 1 Desconectado físicamente' : 'Abrir Pluma Salida'}
                                        >
                                            <FaDoorOpen />
                                            {comandoEjecutando === 'abrir_puerta' ? 'Abriendo...' : (panelConfig.puertas?.p1_d2?.nombre || '🟡 Pluma Salida (P1-R2)')}
                                            {p1Offline && <small style={{ fontSize: '0.68rem', color: '#dc2626' }}> (Offline)</small>}
                                            {panelConfig.puertas?.p1_d2?.accesoGeneral && <span title="Acceso General (Bypass Morosidad)">⭐</span>}
                                        </button>
                                    </div>
                                </div>
                                );
                            })()}

                            {/* Grupo 2: Aperturas Panel 2 (Cuarto Basura DS-K2811) */}
                            {panelConfig.panel2Activo !== false && (() => {
                                const p2Offline = !middlewareState.online || !middlewareState.panel2Connected;
                                return (
                                <div>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: p2Offline ? '#dc2626' : '#7b1fa2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                        <span>🗑️ Panel 2 ({panelConfig.panel2Nombre || 'Cuarto Basura - DS-K2811'}):</span>
                                        {p2Offline && <span style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 'bold' }}>🔴 Desconectado</span>}
                                    </span>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                                        <button 
                                            type="button" 
                                            disabled={comandoEjecutando !== null || p2Offline}
                                            onClick={() => handleEnviarComando('abrir_puerta', { panelId: 'panel_2', puertaId: 1 })}
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                gap: '6px', 
                                                border: `1px solid ${p2Offline ? '#cbd5e1' : '#7b1fa2'}`, 
                                                color: p2Offline ? '#94a3b8' : '#7b1fa2', 
                                                backgroundColor: p2Offline ? '#f8fafc' : '#f3e5f5', 
                                                padding: '8px 6px', 
                                                fontWeight: 'bold', 
                                                borderRadius: '6px', 
                                                cursor: (comandoEjecutando || p2Offline) ? 'not-allowed' : 'pointer', 
                                                fontSize: '0.8rem',
                                                opacity: p2Offline ? 0.6 : 1,
                                                width: '100%'
                                            }}
                                            title={p2Offline ? 'Panel 2 Desconectado físicamente' : 'Abrir Puerta Basura'}
                                        >
                                            <FaDoorOpen />
                                            {comandoEjecutando === 'abrir_puerta' ? 'Abriendo...' : (panelConfig.puertas?.p2_d1?.nombre || '🚪 Puerta Basura (P2-R1)')}
                                            {p2Offline && <small style={{ fontSize: '0.68rem', color: '#dc2626' }}> (Offline)</small>}
                                            {panelConfig.puertas?.p2_d1?.accesoGeneral && <span title="Acceso General (Bypass Morosidad)">⭐</span>}
                                        </button>
                                    </div>
                                </div>
                                );
                            })()}

                            {/* Grupo 3: Aperturas Panel 3 (Hikvision DS-K2814 - 4 Puertas Industrial) */}
                            {panelConfig.panel4pActivo !== false && (() => {
                                const p4Offline = !middlewareState.online || !middlewareState.panel4pConnected;
                                return (
                                <div>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: p4Offline ? '#dc2626' : '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                        <span>🏢 Panel 3 ({panelConfig.panel4pNombre || 'Hikvision DS-K2814 - 4 Puertas'}):</span>
                                        {p4Offline && <span style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 'bold' }}>🔴 Desconectado</span>}
                                    </span>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                        {/* Puerta 1 */}
                                        <button 
                                            type="button" 
                                            disabled={comandoEjecutando !== null || p4Offline}
                                            onClick={() => handleEnviarComando('abrir_puerta', { panelId: 'panel_4puertas', puertaId: 1 })}
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                gap: '4px', 
                                                border: `1px solid ${p4Offline ? '#cbd5e1' : '#16a34a'}`, 
                                                color: p4Offline ? '#94a3b8' : '#16a34a', 
                                                backgroundColor: p4Offline ? '#f8fafc' : '#f0fdf4', 
                                                padding: '8px 4px', 
                                                fontWeight: 'bold', 
                                                borderRadius: '6px', 
                                                cursor: (comandoEjecutando || p4Offline) ? 'not-allowed' : 'pointer', 
                                                fontSize: '0.78rem',
                                                opacity: p4Offline ? 0.6 : 1
                                            }}
                                            title={p4Offline ? 'Panel 4 Puertas Desconectado' : 'Abrir Puerta 1'}
                                        >
                                            <FaDoorOpen />
                                            {comandoEjecutando === 'abrir_puerta' ? 'Abriendo...' : (panelConfig.puertas?.p4_d1?.nombre || 'Puerta 1 (R1)')}
                                            {p4Offline && <small style={{ fontSize: '0.68rem', color: '#dc2626' }}> (Offline)</small>}
                                            {panelConfig.puertas?.p4_d1?.accesoGeneral && <span title="Acceso General (Bypass Morosidad)">⭐</span>}
                                        </button>

                                        {/* Puerta 2 */}
                                        <button 
                                            type="button" 
                                            disabled={comandoEjecutando !== null || p4Offline}
                                            onClick={() => handleEnviarComando('abrir_puerta', { panelId: 'panel_4puertas', puertaId: 2 })}
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                gap: '4px', 
                                                border: `1px solid ${p4Offline ? '#cbd5e1' : '#059669'}`, 
                                                color: p4Offline ? '#94a3b8' : '#059669', 
                                                backgroundColor: p4Offline ? '#f8fafc' : '#ecfdf5', 
                                                padding: '8px 4px', 
                                                fontWeight: 'bold', 
                                                borderRadius: '6px', 
                                                cursor: (comandoEjecutando || p4Offline) ? 'not-allowed' : 'pointer', 
                                                fontSize: '0.78rem',
                                                opacity: p4Offline ? 0.6 : 1
                                            }}
                                            title={p4Offline ? 'Panel 4 Puertas Desconectado' : 'Abrir Puerta 2'}
                                        >
                                            <FaDoorOpen />
                                            {comandoEjecutando === 'abrir_puerta' ? 'Abriendo...' : (panelConfig.puertas?.p4_d2?.nombre || 'Puerta 2 (R2)')}
                                            {p4Offline && <small style={{ fontSize: '0.68rem', color: '#dc2626' }}> (Offline)</small>}
                                            {panelConfig.puertas?.p4_d2?.accesoGeneral && <span title="Acceso General (Bypass Morosidad)">⭐</span>}
                                        </button>

                                        {/* Puerta 3 */}
                                        <button 
                                            type="button" 
                                            disabled={comandoEjecutando !== null || p4Offline}
                                            onClick={() => handleEnviarComando('abrir_puerta', { panelId: 'panel_4puertas', puertaId: 3 })}
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                gap: '4px', 
                                                border: `1px solid ${p4Offline ? '#cbd5e1' : '#0d9488'}`, 
                                                color: p4Offline ? '#94a3b8' : '#0d9488', 
                                                backgroundColor: p4Offline ? '#f8fafc' : '#f0fdfa', 
                                                padding: '8px 4px', 
                                                fontWeight: 'bold', 
                                                borderRadius: '6px', 
                                                cursor: (comandoEjecutando || p4Offline) ? 'not-allowed' : 'pointer', 
                                                fontSize: '0.78rem',
                                                opacity: p4Offline ? 0.6 : 1
                                            }}
                                            title={p4Offline ? 'Panel 4 Puertas Desconectado' : 'Abrir Puerta 3'}
                                        >
                                            <FaDoorOpen />
                                            {comandoEjecutando === 'abrir_puerta' ? 'Abriendo...' : (panelConfig.puertas?.p4_d3?.nombre || 'Puerta 3 (R3)')}
                                            {p4Offline && <small style={{ fontSize: '0.68rem', color: '#dc2626' }}> (Offline)</small>}
                                            {panelConfig.puertas?.p4_d3?.accesoGeneral && <span title="Acceso General (Bypass Morosidad)">⭐</span>}
                                        </button>

                                        {/* Puerta 4 */}
                                        <button 
                                            type="button" 
                                            disabled={comandoEjecutando !== null || p4Offline}
                                            onClick={() => handleEnviarComando('abrir_puerta', { panelId: 'panel_4puertas', puertaId: 4 })}
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                gap: '4px', 
                                                border: `1px solid ${p4Offline ? '#cbd5e1' : '#0284c7'}`, 
                                                color: p4Offline ? '#94a3b8' : '#0284c7', 
                                                backgroundColor: p4Offline ? '#f8fafc' : '#f0f9ff', 
                                                padding: '8px 4px', 
                                                fontWeight: 'bold', 
                                                borderRadius: '6px', 
                                                cursor: (comandoEjecutando || p4Offline) ? 'not-allowed' : 'pointer', 
                                                fontSize: '0.78rem',
                                                opacity: p4Offline ? 0.6 : 1
                                            }}
                                            title={p4Offline ? 'Panel 4 Puertas Desconectado' : 'Abrir Puerta 4'}
                                        >
                                            <FaDoorOpen />
                                            {comandoEjecutando === 'abrir_puerta' ? 'Abriendo...' : (panelConfig.puertas?.p4_d4?.nombre || 'Puerta 4 (R4)')}
                                            {p4Offline && <small style={{ fontSize: '0.68rem', color: '#dc2626' }}> (Offline)</small>}
                                            {panelConfig.puertas?.p4_d4?.accesoGeneral && <span title="Acceso General (Bypass Morosidad)">⭐</span>}
                                        </button>
                                    </div>
                                </div>
                                );
                            })()}

                            {/* Comparación y Auditoría: Panel Físico vs Base de Datos Firebase */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <button 
                                    type="button" 
                                    className="secondary-button" 
                                    disabled={inspeccionando}
                                    onClick={handleInspeccionarPanel}
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        gap: '6px', 
                                        border: '1px solid #0288d1', 
                                        color: '#0288d1', 
                                        backgroundColor: '#e1f5fe', 
                                        padding: '10px 6px',
                                        fontSize: '0.82rem',
                                        fontWeight: 'bold',
                                        borderRadius: '6px'
                                    }}
                                    title="Consultar los tags guardados en la memoria interna física de los paneles Hikvision"
                                >
                                    <FaSearchPlus className={inspeccionando ? 'pulse-green' : ''} />
                                    {inspeccionando ? 'Leyendo Panel...' : '🔍 Tags en Panel (Físico)'}
                                </button>

                                <button 
                                    type="button" 
                                    className="secondary-button" 
                                    disabled={loadingTagsFirebase}
                                    onClick={handleConsultarTagsFirebase}
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        gap: '6px', 
                                        border: '1px solid #1565c0', 
                                        color: '#1565c0', 
                                        backgroundColor: '#e8eaf6', 
                                        padding: '10px 6px',
                                        fontSize: '0.82rem',
                                        fontWeight: 'bold',
                                        borderRadius: '6px'
                                    }}
                                    title="Consultar todos los tags asignados a casas en la Base de Datos de Firebase Cloud"
                                >
                                    <FaDatabase className={loadingTagsFirebase ? 'spin' : ''} />
                                    {loadingTagsFirebase ? 'Cargando BD...' : '☁️ Tags en Firebase (BD)'}
                                </button>
                            </div>

                            <button 
                                type="button" 
                                className="secondary-button" 
                                onClick={() => setShowConsolaComandos(prev => !prev)}
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    gap: '8px', 
                                    border: '1px solid #607d8b', 
                                    color: '#607d8b', 
                                    backgroundColor: showConsolaComandos ? '#eceff1' : 'transparent', 
                                    padding: '10px',
                                    fontWeight: 'bold'
                                }}
                            >
                                <FaTerminal />
                                {showConsolaComandos ? 'Ocultar Consola' : 'Comandos y diagnóstico'}
                            </button>
                        </div>

                        {successMessage && (
                            <div className="note-box" style={{ backgroundColor: '#e8f5e9', borderColor: '#81c784', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '15px', padding: '8px', borderRadius: '6px', fontSize: '0.85rem' }}>
                                <FaCheckCircle /> {successMessage}
                            </div>
                        )}
                        </div>
                    </article>
                </section>

                {/* Historial de Comandos (Fila 2: Ancho Completo) */}
                {showConsolaComandos && (
                    <section className="departamentos-content" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginBottom: '20px' }}>
                        <article className="departamentos-card" style={{ gridColumn: 'span 1' }}>
                            <div className="departamentos-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                <div>
                                    <h2 className="section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FaTerminal style={{ color: '#0288d1' }} /> Historial de Comandos y Diagnóstico (Consola Completa)
                                    </h2>
                                    <p className="help-text" style={{ margin: '4px 0 0 0' }}>
                                        Monitoreo en tiempo real de instrucciones físicas enviadas a los paneles Hikvision.
                                    </p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: '500' }}>
                                        {comandosFiltrados.length} de {comandosRecientes.length} comandos
                                    </span>
                                    <button 
                                        type="button" 
                                        onClick={handleExportarComandosCsv}
                                        disabled={comandosFiltrados.length === 0}
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '6px', 
                                            padding: '6px 12px', 
                                            borderRadius: '6px', 
                                            fontSize: '0.82rem', 
                                            cursor: comandosFiltrados.length === 0 ? 'not-allowed' : 'pointer', 
                                            backgroundColor: '#0288d1', 
                                            border: 'none', 
                                            color: '#fff', 
                                            fontWeight: 'bold',
                                            boxShadow: '0 2px 4px rgba(2, 136, 209, 0.2)'
                                        }}
                                        title="Exportar los comandos filtrados a formato CSV"
                                    >
                                        <FaDownload /> Exportar Logs (CSV)
                                    </button>
                                </div>
                            </div>

                            {/* Barra de Filtros del Historial de Comandos */}
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '10px',
                                alignItems: 'center',
                                padding: '10px 12px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '8px',
                                margin: '12px 0',
                                border: '1px solid #e2e8f0'
                            }}>
                                <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                                    <input 
                                        type="text"
                                        placeholder="🔍 Buscar comando, detalle, panel o ID..."
                                        value={filtroCmdTexto}
                                        onChange={(e) => setFiltroCmdTexto(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '7px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.82rem',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div style={{ minWidth: '140px' }}>
                                    <select
                                        value={filtroCmdEstatus}
                                        onChange={(e) => setFiltroCmdEstatus(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '7px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.82rem',
                                            backgroundColor: '#fff',
                                            outline: 'none',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="todos">Todos los estatus</option>
                                        <option value="completado">Completado</option>
                                        <option value="pendiente">Pendiente</option>
                                        <option value="ejecutando">Ejecutando</option>
                                        <option value="error">Error</option>
                                    </select>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: '#64748b' }}>
                                    <span>Desde:</span>
                                    <input 
                                        type="date"
                                        value={filtroCmdFechaInicio}
                                        onChange={(e) => setFiltroCmdFechaInicio(e.target.value)}
                                        style={{
                                            padding: '6px 8px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.82rem',
                                            outline: 'none'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: '#64748b' }}>
                                    <span>Hasta:</span>
                                    <input 
                                        type="date"
                                        value={filtroCmdFechaFin}
                                        onChange={(e) => setFiltroCmdFechaFin(e.target.value)}
                                        style={{
                                            padding: '6px 8px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.82rem',
                                            outline: 'none'
                                        }}
                                    />
                                </div>

                                {(filtroCmdTexto || filtroCmdEstatus !== 'todos' || filtroCmdFechaInicio || filtroCmdFechaFin) && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFiltroCmdTexto('');
                                            setFiltroCmdEstatus('todos');
                                            setFiltroCmdFechaInicio('');
                                            setFiltroCmdFechaFin('');
                                        }}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                            backgroundColor: '#fff',
                                            color: '#ef4444',
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Limpiar Filtros
                                    </button>
                                )}
                            </div>

                            <div className="table-responsive" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid #e0e0e0', color: '#757575', backgroundColor: '#f8fafc' }}>
                                            <th style={{ padding: '8px 10px' }}>Comando</th>
                                            <th style={{ padding: '8px 10px' }}>Estatus</th>
                                            <th style={{ padding: '8px 10px' }}>Hora (CST)</th>
                                            <th style={{ padding: '8px 10px' }}>Detalle/Error del Servidor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingComandos ? (
                                            <tr>
                                                <td colSpan="4" style={{ padding: '14px', textAlign: 'center', color: '#757575' }}>Cargando comandos...</td>
                                            </tr>
                                        ) : comandosFiltrados.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" style={{ padding: '14px', textAlign: 'center', color: '#757575' }}>
                                                    {comandosRecientes.length === 0 ? 'No hay comandos registrados.' : 'No se encontraron comandos que coincidan con los filtros seleccionados.'}
                                                </td>
                                            </tr>
                                        ) : (
                                            comandosFiltrados.map((cmd) => (
                                                <tr key={cmd.id} style={{ borderBottom: '1px solid #eeeeee' }}>
                                                    <td style={{ padding: '8px 10px', fontWeight: 'bold' }}>
                                                        {cmd.comando === 'abrir_puerta' ? (
                                                            <span>
                                                                Apertura {cmd.parametros?.panelId === 'panel_2' ? (
                                                                    <span style={{ color: '#7b1fa2' }}>[Panel 2: Basura - R{cmd.parametros?.puertaId || 1}]</span>
                                                                ) : (
                                                                    <span style={{ color: '#0288d1' }}>[Panel 1: Vehicular - R{cmd.parametros?.puertaId || 1}]</span>
                                                                )}
                                                            </span>
                                                        ) : cmd.comando === 'sincronizar_todo' ? (
                                                            <span>Sincronización <span style={{ color: '#2e7d32' }}>[Multi-Panel BD]</span></span>
                                                        ) : cmd.comando === 'solicitar_usuarios' ? (
                                                            <span>Inspección <span style={{ color: '#0288d1' }}>[Memoria Física]</span></span>
                                                        ) : cmd.comando}
                                                    </td>
                                                    <td style={{ padding: '8px 10px' }}>
                                                        <span style={{
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 'bold',
                                                            backgroundColor: 
                                                                cmd.estatus === 'completado' ? '#e8f5e9' :
                                                                cmd.estatus === 'pendiente' ? '#fff3e0' :
                                                                cmd.estatus === 'ejecutando' ? '#e3f2fd' : '#ffebee',
                                                            color:
                                                                cmd.estatus === 'completado' ? '#2e7d32' :
                                                                cmd.estatus === 'pendiente' ? '#e65100' :
                                                                cmd.estatus === 'ejecutando' ? '#0d47a1' : '#c62828'
                                                        }}>
                                                            {cmd.estatus.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '8px 10px', color: '#757575', whiteSpace: 'nowrap' }}>
                                                        {cmd.creadoEn ? cmd.creadoEn.toLocaleTimeString('es-MX', { timeZone: 'America/Mexico_City' }) : ''}
                                                    </td>
                                                    <td style={{ padding: '8px 10px', color: cmd.estatus === 'error' ? '#c62828' : cmd.estatus === 'ejecutando' ? '#0d47a1' : '#616161' }}>
                                                        {cmd.estatus === 'error' 
                                                            ? (cmd.errorMsg || 'Error de comunicación') 
                                                            : (cmd.detalle || 'Ejecutado exitosamente sin observaciones')}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </article>
                    </section>
                )}

                {/* Visor de Logs de Acceso en Tiempo Real (Fila 3: Ancho Completo) */}
                <section className="departamentos-content" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                    <article className="departamentos-card" style={{ gridColumn: 'span 1' }}>
                        <div className="departamentos-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 className="section-title" style={{ margin: 0 }}><FaHistory /> Accesos Recientes en Caseta</h2>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button 
                                    type="button" 
                                    onClick={handleRefrescarHistorial}
                                    disabled={refrescandoLogs}
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '6px', 
                                        padding: '6px 12px', 
                                        borderRadius: '4px', 
                                        fontSize: '0.85rem', 
                                        cursor: refrescandoLogs ? 'not-allowed' : 'pointer', 
                                        margin: 0, 
                                        backgroundColor: '#f1f5f9', 
                                        border: '1px solid #cbd5e1', 
                                        color: '#334155', 
                                        fontWeight: '600',
                                        transition: 'all 0.2s'
                                    }}
                                    title="Actualizar manualmente los accesos recientes desde la base de datos"
                                >
                                    <FaSync className={refrescandoLogs ? 'spin' : ''} />
                                    {refrescandoLogs ? 'Refrescando...' : 'Refrescar'}
                                </button>
                                <button 
                                    type="button" 
                                    className="secondary-button" 
                                    onClick={handleBorrarHistorialLogs}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #f44336', color: '#f44336', backgroundColor: 'transparent', padding: '6px 12px', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', margin: 0 }}
                                >
                                    <FaTrashAlt /> Borrar Historial
                                </button>
                                <button 
                                    type="button" 
                                    className="primary-button" 
                                    onClick={handleRFIDExport}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', margin: 0 }}
                                >
                                    <FaDownload /> Descargar Morosos (CSV)
                                </button>
                                <button 
                                    type="button" 
                                    onClick={handleExportarLogsCSV}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', margin: 0, backgroundColor: '#0288d1', border: 'none', color: '#fff', fontWeight: 'bold' }}
                                >
                                    <FaDownload /> Descargar Registros (CSV)
                                </button>
                            </div>
                        </div>
                        <p className="help-text">Visualiza las lecturas físicas de las plumas y accesos peatonales registradas en tiempo real por el panel de control.</p>

                        {/* Barra de Filtros Premium (Estética Zen) */}
                        <div style={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '16px',
                            marginTop: '15px',
                            marginBottom: '15px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)'
                        }}>
                            {/* Buscador Principal y Botón de Limpieza */}
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '12px',
                                alignItems: 'center'
                            }}>
                                <div style={{ flex: '1', minWidth: '240px', position: 'relative' }}>
                                    <input
                                        type="text"
                                        placeholder="Buscar por NIP, residente, tag RFID, tipo de acceso, casa..."
                                        value={filtroTexto}
                                        onChange={(e) => setFiltroTexto(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.85rem',
                                            backgroundColor: '#ffffff',
                                            outline: 'none',
                                            transition: 'border-color 0.2s, box-shadow 0.2s',
                                            boxSizing: 'border-box',
                                            height: '29px'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#6366f1';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.15)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#cbd5e1';
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>
                                {(filtroTexto || filtroFechaInicio || filtroFechaFin || filtroHoraInicio || filtroHoraFin) && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFiltroTexto('');
                                            setFiltroFechaInicio('');
                                            setFiltroFechaFin('');
                                            setFiltroHoraInicio('');
                                            setFiltroHoraFin('');
                                            setPaginaActual(1);
                                        }}
                                        style={{
                                            padding: '4px 16px',
                                            backgroundColor: '#f1f5f9',
                                            color: '#475569',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            fontSize: '0.82rem',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s',
                                            fontWeight: '500',
                                            height: '29px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxSizing: 'border-box'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#e2e8f0'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                                    >
                                        Limpiar Filtros
                                    </button>
                                )}
                            </div>

                            {/* Filtros Temporales (Fechas y Horarios) */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                gap: '16px',
                                width: '100%',
                                marginTop: '4px'
                            }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>Desde Fecha</label>
                                    <input
                                        type="date"
                                        value={filtroFechaInicio}
                                        onChange={(e) => setFiltroFechaInicio(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.85rem',
                                            outline: 'none',
                                            backgroundColor: '#ffffff',
                                            boxSizing: 'border-box',
                                            height: '29px'
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>Hasta Fecha</label>
                                    <input
                                        type="date"
                                        value={filtroFechaFin}
                                        onChange={(e) => setFiltroFechaFin(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.85rem',
                                            outline: 'none',
                                            backgroundColor: '#ffffff',
                                            boxSizing: 'border-box',
                                            height: '29px'
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>Desde Hora</label>
                                    <input
                                        type="time"
                                        value={filtroHoraInicio}
                                        onChange={(e) => setFiltroHoraInicio(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.85rem',
                                            outline: 'none',
                                            backgroundColor: '#ffffff',
                                            boxSizing: 'border-box',
                                            height: '29px'
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>Hasta Hora</label>
                                    <input
                                        type="time"
                                        value={filtroHoraFin}
                                        onChange={(e) => setFiltroHoraFin(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '0.85rem',
                                            outline: 'none',
                                            backgroundColor: '#ffffff',
                                            boxSizing: 'border-box',
                                            height: '29px'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive" style={{ marginTop: '15px', overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #e0e0e0', color: '#757575' }}>
                                        <th style={{ padding: '10px' }}>Fecha y Hora</th>
                                        <th style={{ padding: '10px' }}>Casa / Lote</th>
                                        <th style={{ padding: '10px' }}>Residente</th>
                                        <th style={{ padding: '10px' }}>Tag RFID</th>
                                        <th style={{ padding: '10px' }}>Punto de Acceso</th>
                                        <th style={{ padding: '10px' }}>Tipo</th>
                                        <th style={{ padding: '10px' }}>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingLogs ? (
                                        <tr>
                                            <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#757575' }}>
                                                Cargando eventos de acceso...
                                            </td>
                                        </tr>
                                    ) : logsFiltrados.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#757575' }}>
                                                No se han registrado accesos que coincidan con los filtros.
                                            </td>
                                        </tr>
                                    ) : (
                                        logsPaginados.map((log) => {
                                            const isDenegado = log.estadoAcceso && log.estadoAcceso.startsWith('denegado');
                                            const puntoInfo = resolverPuntoAccesoInfo(log, panelConfig);
                                            return (
                                                <tr key={log.id} style={{ borderBottom: '1px solid #eeeeee', backgroundColor: isDenegado ? '#fff8f8' : 'transparent', transition: 'background-color 0.2s' }}>
                                                    <td style={{ padding: '10px', fontSize: '0.85rem', color: '#475569' }}>
                                                        {log.timestamp ? log.timestamp.toLocaleString('es-MX') : ''}
                                                    </td>
                                                    <td style={{ padding: '10px' }}><strong>{log.numeroCasa || 'S/N'}</strong></td>
                                                    <td style={{ padding: '10px' }}>{log.residenteNombre || 'Desconocido'}</td>
                                                    <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '0.85rem' }}>{log.rfid_tag}</td>
                                                    <td style={{ padding: '10px' }}>
                                                        <span style={{ 
                                                            display: 'inline-flex', 
                                                            alignItems: 'center', 
                                                            gap: '6px', 
                                                            fontWeight: '600', 
                                                            fontSize: '0.82rem', 
                                                            color: puntoInfo.color 
                                                        }}>
                                                            <span>{puntoInfo.icono}</span>
                                                            <span>{puntoInfo.nombrePuerta}</span>
                                                            <span style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: '500' }}>
                                                                ({puntoInfo.nombrePanel})
                                                            </span>
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '10px' }}>
                                                        {log.tipoAcceso === 'qr' ? (
                                                            <span className="tag" style={{ backgroundColor: '#f3e5f5', color: '#6a1b9a', padding: '3px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                <FaQrcode /> QR
                                                            </span>
                                                        ) : log.tipoAcceso === 'tarjeta' ? (
                                                            <span className="tag" style={{ backgroundColor: '#e0f7fa', color: '#006064', padding: '3px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: '500' }}>
                                                                💳 Tag UHF
                                                            </span>
                                                        ) : log.tipoAcceso === 'nip' ? (
                                                            <span className="tag" style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>
                                                                ⌨️ NIP
                                                            </span>
                                                        ) : (
                                                            <span className="tag" style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>
                                                                👤 Rostro
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '10px' }}>
                                                        {log.estadoAcceso === 'concedido_acceso_general' ? (
                                                            <span title={log.motivo || 'Acceso concedido por bypass de morosidad'} className="tag" style={{ backgroundColor: '#f0fdf4', color: '#15803d', padding: '3px 8px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 'bold', border: '1px solid #86efac', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                                                ⭐ Acceso General (Bypass)
                                                            </span>
                                                        ) : log.estadoAcceso === 'denegado_moroso' ? (
                                                            <span title={log.motivo || 'Casa con adeudo'} className="tag" style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '3px 8px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 'bold', border: '1px solid #ffcdd2', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                                                🚫 Denegado (Moroso)
                                                            </span>
                                                        ) : log.estadoAcceso === 'denegado_regla_oro' ? (
                                                            <span title={log.motivo || 'Sin entrada previa'} className="tag" style={{ backgroundColor: '#fff3e0', color: '#e65100', padding: '3px 8px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 'bold', border: '1px solid #ffe0b2', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                                                ⚠️ Regla de Oro (Sin Entrada)
                                                            </span>
                                                        ) : log.estadoAcceso === 'denegado_expirado' ? (
                                                            <span title={log.motivo || 'Expirado'} className="tag" style={{ backgroundColor: '#fff8e1', color: '#b45309', padding: '3px 8px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 'bold', border: '1px solid #ffecb3', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                                                ⏳ Denegado (Expirado)
                                                            </span>
                                                        ) : log.estadoAcceso === 'denegado_cupo' ? (
                                                            <span title={log.motivo || 'Cupo agotado'} className="tag" style={{ backgroundColor: '#ede7f6', color: '#4527a0', padding: '3px 8px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 'bold', border: '1px solid #d1c4e9', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                                                🔢 Cupo Agotado
                                                            </span>
                                                        ) : log.estadoAcceso === 'denegado_no_registrado' ? (
                                                            <span title={log.motivo || 'No registrado'} className="tag" style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '3px 8px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 'bold', border: '1px solid #ffcdd2', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                                                ❌ No Registrado
                                                            </span>
                                                        ) : isDenegado ? (
                                                            <span title={log.motivo || 'Acceso denegado'} className="tag" style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '3px 8px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 'bold', border: '1px solid #ffcdd2', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                                                ❌ Denegado
                                                            </span>
                                                        ) : (
                                                            <span title={log.motivo || 'Acceso concedido'} className="tag" style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '3px 8px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 'bold', border: '1px solid #c8e6c9', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                                                ✅ Concedido
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Controles de Paginación Premium (Estética Zen) */}
                        {logsFiltrados.length > 0 && (
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: '16px',
                                padding: '8px 4px',
                                borderTop: '1px solid #f1f5f9',
                                gap: '12px'
                            }}>
                                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                    Mostrando <strong>{Math.min((paginaActual - 1) * registrosPorPagina + 1, logsFiltrados.length)}</strong> a <strong>{Math.min(paginaActual * registrosPorPagina, logsFiltrados.length)}</strong> de <strong>{logsFiltrados.length}</strong> registros
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <button
                                        type="button"
                                        disabled={paginaActual === 1}
                                        onClick={() => setPaginaActual(prev => Math.max(prev - 1, 1))}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            backgroundColor: paginaActual === 1 ? '#f8fafc' : '#ffffff',
                                            color: paginaActual === 1 ? '#94a3b8' : '#334155',
                                            fontSize: '0.85rem',
                                            cursor: paginaActual === 1 ? 'not-allowed' : 'pointer',
                                            transition: 'background-color 0.2s, color 0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (paginaActual !== 1) e.target.style.backgroundColor = '#f1f5f9';
                                        }}
                                        onMouseLeave={(e) => {
                                            if (paginaActual !== 1) e.target.style.backgroundColor = '#ffffff';
                                        }}
                                    >
                                        Anterior
                                    </button>
                                    
                                    {/* Páginas numéricas compactas */}
                                    {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                                        .filter(p => p === 1 || p === totalPaginas || Math.abs(p - paginaActual) <= 1)
                                        .map((p, index, arr) => {
                                            const showEllipsis = index > 0 && p - arr[index - 1] > 1;
                                            return (
                                                <React.Fragment key={p}>
                                                    {showEllipsis && <span style={{ color: '#94a3b8', padding: '0 4px' }}>...</span>}
                                                    <button
                                                        type="button"
                                                        onClick={() => setPaginaActual(p)}
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '6px',
                                                            border: '1px solid',
                                                            borderColor: paginaActual === p ? '#6366f1' : '#cbd5e1',
                                                            backgroundColor: paginaActual === p ? '#6366f1' : '#ffffff',
                                                            color: paginaActual === p ? '#ffffff' : '#334155',
                                                            fontSize: '0.85rem',
                                                            fontWeight: paginaActual === p ? '600' : '400',
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.2s, color 0.2s, border-color 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (paginaActual !== p) e.target.style.backgroundColor = '#f1f5f9';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (paginaActual !== p) e.target.style.backgroundColor = '#ffffff';
                                                        }}
                                                    >
                                                        {p}
                                                    </button>
                                                </React.Fragment>
                                            );
                                        })
                                    }

                                    <button
                                        type="button"
                                        disabled={paginaActual === totalPaginas}
                                        onClick={() => setPaginaActual(prev => Math.min(prev + 1, totalPaginas))}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            backgroundColor: paginaActual === totalPaginas ? '#f8fafc' : '#ffffff',
                                            color: paginaActual === totalPaginas ? '#94a3b8' : '#334155',
                                            fontSize: '0.85rem',
                                            cursor: paginaActual === totalPaginas ? 'not-allowed' : 'pointer',
                                            transition: 'background-color 0.2s, color 0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (paginaActual !== totalPaginas) e.target.style.backgroundColor = '#f1f5f9';
                                        }}
                                        onMouseLeave={(e) => {
                                            if (paginaActual !== totalPaginas) e.target.style.backgroundColor = '#ffffff';
                                        }}
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            </div>
                        )}
                    </article>
                </section>
            </div>

            {/* Modal de Inspección de Memoria del Panel Físico (Lupa) */}
            {showInspeccionModal && (
                <div className="modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
                    justifyContent: 'center', alignItems: 'center', zIndex: 1000,
                    backdropFilter: 'blur(4px)'
                }}>
                    <div className="departamentos-card" style={{
                        width: '95%', maxWidth: '880px', maxHeight: '85%',
                        display: 'flex', flexDirection: 'column', padding: '20px',
                        borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                        backgroundColor: '#ffffff'
                    }}>
                        <div className="departamentos-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h2 className="section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FaSearchPlus style={{ color: '#4caf50' }} /> Usuarios y Tags RFID en Memoria del Panel (Físico)
                            </h2>
                            <button 
                                onClick={() => setShowInspeccionModal(false)}
                                style={{
                                    border: 'none', backgroundColor: 'transparent', fontSize: '1.5rem',
                                    cursor: 'pointer', color: '#757575', fontWeight: 'bold'
                                }}
                            >
                                &times;
                            </button>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                            <p className="help-text" style={{ margin: 0, flex: 1 }}>Visualiza en tiempo real los residentes y tags RFID autorizados en la memoria física de los paneles.</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {usuariosPanel.length > 0 && (
                                    <span className="badge-count" style={{
                                        backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '4px 10px',
                                        borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold', border: '1px solid #c8e6c9',
                                        display: 'inline-flex', alignItems: 'center'
                                    }}>
                                        {usuariosPanel.length} residentes registrados ({usuariosPanel.filter(u => u.cardno && u.cardno !== 0 && u.cardno !== '0' && u.cardno !== 'Sin Tarjeta').length} con tarjeta RFID)
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={handleExportarPanelFisicoCsv}
                                    disabled={usuariosPanel.length === 0}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '5px 10px',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        cursor: usuariosPanel.length === 0 ? 'not-allowed' : 'pointer',
                                        backgroundColor: '#0288d1',
                                        border: 'none',
                                        color: '#fff',
                                        fontWeight: 'bold'
                                    }}
                                    title="Descargar lista de memoria del panel en CSV"
                                >
                                    <FaDownload /> Exportar CSV
                                </button>
                            </div>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <input 
                                type="text"
                                placeholder="🔍 Buscar por nombre o ID de usuario (UID)..."
                                value={filtroBuscador}
                                onChange={(e) => setFiltroBuscador(e.target.value)}
                                style={{
                                    width: '100%', padding: '10px 14px', borderRadius: '6px',
                                    border: '1px solid #cccccc', fontSize: '0.9rem', outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        <div className="table-responsive" style={{ flex: 1, overflowY: 'auto', border: '1px solid #eeeeee', borderRadius: '6px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0', color: '#757575', whiteSpace: 'nowrap' }}>
                                        <th style={{ padding: '8px 12px', width: '100px' }}>ID Residente</th>
                                        <th style={{ padding: '8px 12px', minWidth: '220px', width: '220px' }}>Nombre</th>
                                        <th style={{ padding: '8px 12px', width: '120px' }}>Tarjeta (RFID)</th>
                                        <th style={{ padding: '8px 12px', width: '100px' }}>NIP / Clave</th>
                                        <th style={{ padding: '8px 12px', minWidth: '130px', width: '130px' }}>Privilegio</th>
                                        <th style={{ padding: '8px 12px', width: '100px', textAlign: 'center' }}>Grupo Acceso</th>
                                        <th style={{ padding: '8px 12px', minWidth: '110px', width: '110px' }}>Horarios [TZ]</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usuariosPanel
                                        .filter(u => {
                                            const impreso = u.cardno ? convertirTagAntenaAImpreso(u.cardno) : null;
                                            return (
                                                String(u.name || '').toLowerCase().includes(filtroBuscador.toLowerCase()) ||
                                                String(u.userId || '').includes(filtroBuscador) ||
                                                String(u.uid || '').includes(filtroBuscador) ||
                                                String(u.cardno || '').includes(filtroBuscador) ||
                                                (impreso && impreso.includes(filtroBuscador))
                                            );
                                        })
                                        .map((u) => {
                                            const impreso = u.cardno && u.cardno !== 'Sin Tarjeta' && u.cardno !== 0 && u.cardno !== '0'
                                                ? convertirTagAntenaAImpreso(u.cardno)
                                                : null;
                                            return (
                                                <tr key={u.uid} style={{ borderBottom: '1px solid #eeeeee', whiteSpace: 'nowrap' }}>
                                                    <td style={{ padding: '8px 12px', fontWeight: 'bold', color: '#2e7d32' }}>{u.userId || 'S/N'}</td>
                                                    <td style={{ padding: '8px 12px', fontWeight: 'bold', minWidth: '220px', width: '220px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{u.name || 'Sin registro'}</td>
                                                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#1565c0' }}>
                                                        {u.cardno && u.cardno !== 0 && u.cardno !== '0' && u.cardno !== 'Sin Tarjeta' ? (
                                                            <div>
                                                                <span style={{ fontWeight: 'bold' }}>{u.cardno}</span>
                                                                {impreso && impreso !== String(u.cardno) && (
                                                                    <div style={{ fontSize: '0.72rem', color: '#4b5563', fontFamily: 'sans-serif' }}>
                                                                        Impreso: <strong>{impreso}</strong>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#9ca3af' }}>Sin Tarjeta</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{u.password || 'Ninguno'}</td>
                                                <td style={{ padding: '8px 12px', minWidth: '130px', width: '130px' }}>
                                                    <span style={{
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        backgroundColor: u.role === 14 ? '#ffebee' : '#e3f2fd',
                                                        color: u.role === 14 ? '#c62828' : '#0d47a1',
                                                        display: 'inline-block',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {u.role === 14 ? 'Administrador' : 'Usuario Normal'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 'bold' }}>
                                                    {u.group !== undefined ? u.group : '1'}
                                                </td>
                                                <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#37474f', minWidth: '110px', width: '110px' }}>
                                                    {u.tz1 !== undefined ? `[${u.tz1}, ${u.tz2}, ${u.tz3}]` : '[1, 4, 0]'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {usuariosPanel.length === 0 && (
                                        <tr>
                                            <td colSpan="7" style={{ padding: '24px 16px', textAlign: 'center', color: '#64748b' }}>
                                                <div style={{ fontWeight: '600', marginBottom: '4px', color: '#0f172a' }}>
                                                    No se encontraron tarjetas grabadas en la memoria local de caseta
                                                </div>
                                                <div style={{ fontSize: '0.8rem' }}>
                                                    Presiona el botón <strong>"Iniciar Sincronización con BD"</strong> para transferir las tarjetas de residentes autorizados hacia los paneles físicos y la caché de caseta.
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
                            <button 
                                className="primary-button"
                                onClick={() => setShowInspeccionModal(false)}
                                style={{ padding: '8px 16px', fontSize: '0.9rem' }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Consulta de Tags Registrados en Firebase (BD Cloud) */}
            {showTagsFirebaseModal && (
                <div className="modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
                    justifyContent: 'center', alignItems: 'center', zIndex: 1000,
                    backdropFilter: 'blur(4px)'
                }}>
                    <div className="departamentos-card" style={{
                        width: '95%', maxWidth: '920px', maxHeight: '85%',
                        display: 'flex', flexDirection: 'column', padding: '20px',
                        borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                        backgroundColor: '#ffffff'
                    }}>
                        <div className="departamentos-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h2 className="section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FaDatabase style={{ color: '#1565c0' }} /> Tags Registrados en Firebase (Base de Datos Cloud)
                            </h2>
                            <button 
                                onClick={() => setShowTagsFirebaseModal(false)}
                                style={{
                                    border: 'none', backgroundColor: 'transparent', fontSize: '1.5rem',
                                    cursor: 'pointer', color: '#757575', fontWeight: 'bold'
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                            <p className="help-text" style={{ margin: 0, flex: 1 }}>
                                Lista completa de tags asignados a cada vivienda en la nube. Compara esta información contra la leída directamente de la memoria física de los paneles.
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {tagsFirebase.length > 0 && (
                                    <span className="badge-count" style={{
                                        backgroundColor: '#e8eaf6', color: '#1565c0', padding: '4px 10px',
                                        borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold', border: '1px solid #c5cae9',
                                        display: 'inline-flex', alignItems: 'center'
                                    }}>
                                        {tagsFirebase.length} tags en BD ({tagsFirebase.filter(t => !t.adeudo && !t.bloqueado).length} autorizados, {tagsFirebase.filter(t => t.adeudo || t.bloqueado).length} morosos/bloqueados)
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={handleExportarTagsFirebaseCsv}
                                    disabled={tagsFirebase.length === 0}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '5px 10px',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        cursor: tagsFirebase.length === 0 ? 'not-allowed' : 'pointer',
                                        backgroundColor: '#1565c0',
                                        border: 'none',
                                        color: '#fff',
                                        fontWeight: 'bold'
                                    }}
                                    title="Descargar lista de tags de Firebase en CSV"
                                >
                                    <FaDownload /> Exportar CSV
                                </button>
                            </div>
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                            <input 
                                type="text"
                                placeholder="🔍 Buscar por número de casa, titular, tag antena o tag impreso..."
                                value={filtroBuscadorFirebase}
                                onChange={(e) => setFiltroBuscadorFirebase(e.target.value)}
                                style={{
                                    width: '100%', padding: '9px 12px', borderRadius: '6px',
                                    border: '1px solid #cccccc', fontSize: '0.88rem', outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        <div className="table-responsive" style={{ flex: 1, overflowY: 'auto', border: '1px solid #eeeeee', borderRadius: '6px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', whiteSpace: 'nowrap' }}>
                                        <th style={{ padding: '8px 12px', width: '90px' }}>Casa / Depto</th>
                                        <th style={{ padding: '8px 12px', minWidth: '200px' }}>Residente / Titular</th>
                                        <th style={{ padding: '8px 12px', width: '140px' }}>Asignación</th>
                                        <th style={{ padding: '8px 12px', width: '150px' }}>Tag RFID</th>
                                        <th style={{ padding: '8px 12px', width: '130px', textAlign: 'center' }}>Estado Financiero</th>
                                        <th style={{ padding: '8px 12px', width: '130px', textAlign: 'center' }}>Estatus en Panel</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingTagsFirebase ? (
                                        <tr>
                                            <td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#757575' }}>
                                                Consultando tags desde Firebase Cloud...
                                            </td>
                                        </tr>
                                    ) : (
                                        tagsFirebase
                                            .filter(t => {
                                                const term = filtroBuscadorFirebase.toLowerCase().trim();
                                                if (!term) return true;
                                                return (
                                                    String(t.numeroCasa || '').toLowerCase().includes(term) ||
                                                    String(t.titular || '').toLowerCase().includes(term) ||
                                                    String(t.tipoEtiqueta || '').toLowerCase().includes(term) ||
                                                    String(t.tagAntena || '').includes(term) ||
                                                    (t.tagImpreso && String(t.tagImpreso).includes(term))
                                                );
                                            })
                                            .map((t) => {
                                                const esAutorizado = !t.adeudo && !t.bloqueado;
                                                return (
                                                    <tr key={t.id} style={{ borderBottom: '1px solid #eeeeee' }}>
                                                        <td style={{ padding: '8px 12px', fontWeight: 'bold', color: '#1565c0' }}>
                                                            {t.numeroCasa || 'S/N'}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', fontWeight: '600' }}>
                                                            {t.titular || 'Sin Asignar'}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', fontSize: '0.8rem', color: '#64748b' }}>
                                                            {t.tipoEtiqueta}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#0d47a1' }}>
                                                            <div style={{ fontWeight: 'bold' }}>{t.tagAntena}</div>
                                                            {t.tagImpreso && t.tagImpreso !== t.tagAntena && (
                                                                <div style={{ fontSize: '0.72rem', color: '#64748b', fontFamily: 'sans-serif' }}>
                                                                    Impreso: <strong>{t.tagImpreso}</strong>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                            <span 
                                                                title={t.montoAdeudo > 0 ? (t.adeudo ? `Adeudo moroso: $${t.montoAdeudo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : `Adeudo en gracia: $${t.montoAdeudo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`) : 'Sin adeudo'}
                                                                style={{
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 'bold',
                                                                    backgroundColor: t.adeudo ? '#ffebee' : '#e8f5e9',
                                                                    color: t.adeudo ? '#c62828' : '#2e7d32'
                                                                }}
                                                            >
                                                                {t.adeudo ? 'Con Adeudo' : 'Al Corriente'}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                            <span style={{
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold',
                                                                backgroundColor: esAutorizado ? '#e8f5e9' : '#fff3e0',
                                                                color: esAutorizado ? '#2e7d32' : '#e65100'
                                                            }}>
                                                                {esAutorizado ? 'Grabado (Activo)' : 'Bloqueado'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                    )}
                                    {!loadingTagsFirebase && tagsFirebase.length === 0 && (
                                        <tr>
                                            <td colSpan="6" style={{ padding: '20px', textAlign: 'center', color: '#757575' }}>
                                                No se encontraron tags asignados en las casas de este fraccionamiento.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
                            <button 
                                className="primary-button"
                                onClick={() => setShowTagsFirebaseModal(false)}
                                style={{ padding: '8px 16px', fontSize: '0.9rem' }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Estilos dinámicos para el hover de Tooltips */}
            <style>{`
                .tooltip-container:hover .tooltip-text {
                    visibility: visible !important;
                    opacity: 1 !important;
                }
                @keyframes pulse-green-glow {
                    0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
                }
                .pulse-green {
                    animation: pulse-green-glow 2s infinite;
                }
            `}</style>
        </div>
    );
};

export default DepartamentosRFID;
